"use client";

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { GoogleSignIn } from "./google-sign-in";
import { HomePage } from "./home-page";
import { VerbaloreMark } from "./verbalore-mark";
import {
  ALL_EXPRESSION_STUDIES,
  BOUNDARY_STUDY,
  EXPRESSION_STUDY_BY_ID,
  type ExpressionStudy,
} from "../content/expression-studies";
import {
  READINGS,
  READING_BY_ID,
  READING_INTENTS,
  INITIAL_LESSON_LEXICON,
  findDictionaryEntry,
  findWordInsight,
  loadLessonLexicon,
  readingTokenIds,
  recommendReadings,
} from "../content/catalog";
import type {
  LessonLexicon,
  LessonLexiconEntry,
  Reading,
  ReadingIntent,
  ReadingSegment as StorySegment,
  WordInsight,
} from "../content/types";

type Language = "English" | "Hindi" | "Punjabi";
type PanelMode = "empty" | "word" | "library" | "profile";
type ExperienceMode = "read" | "practice";
type PracticeView = "words" | "expression";
type ReadingSize = "compact" | "balanced" | "generous";

type SelectionState = {
  word: string;
  key: string;
  tokenId: string;
  paragraphId: string;
  sentence: string;
  curated: boolean;
};

type Profile = {
  id: string;
  displayName: string;
  preferredLanguage: Language;
  explorationDepth: string;
  assistanceMode: string;
};

type VocabularyItem = {
  id?: number;
  profileId?: string;
  word: string;
  displayWord: string;
  status: string;
  meaning: string;
  sourceSentence: string;
  sourceAnchor: string;
  lastOpenedSection?: string;
  createdAt?: string;
  updatedAt?: string;
};

type VocabularyContext = {
  id?: number;
  profileId?: string;
  word: string;
  displayWord: string;
  meaning: string;
  sourceSentence: string;
  sourceAnchor: string;
  sourceType: "authored" | "private_import";
  sourceId: string;
  createdAt?: string;
  updatedAt?: string;
};

type ReadingProgress = {
  lessonId: string;
  paragraphId: string;
  percentage: number;
  selectedWord?: string | null;
  selectedAnchor?: string | null;
  updatedAt?: string;
};

type PracticeAttempt = {
  id: number;
  promptId: string;
  draft: string;
  revision: string | null;
  assistanceMode: string;
  signals: string;
  status: string;
  updatedAt: string;
};

type PracticeSignal = {
  id: string;
  label: string;
  met: boolean;
  guidance: string;
};

type PracticeAssessment = {
  status: "empty" | "not-assessed" | "assessed";
  message: string;
  signals: PracticeSignal[];
};

type ExpressionWork = {
  draft: string;
  revision: string;
  checked: boolean;
  stanceId: string;
};

type ReviewEvidence = {
  status: "Encountered" | "Active" | "Owned";
  successfulChecks: number;
  useAttempts: number;
  naturalUses: number;
  notYet: number;
  spacedRecall: boolean;
  nextStep: "recall" | "contrast" | "use" | "natural";
  nextReviewAt: string;
  due: boolean;
  canBecomeActive: boolean;
  canBecomeOwned: boolean;
};

type WordReview = {
  item: VocabularyItem & { createdAt: string; updatedAt: string };
  prompt: null | {
    word: string;
    recall: { stem: string; options: string[] };
    contrast: { question: string; options: string[] };
    use: {
      direction: string;
      criteria: Array<{ id: string; label: string }>;
    };
    naturalCue: string;
  };
  evidence: ReviewEvidence;
};

type SessionUser = {
  id: string;
  subject: string;
  email: string;
  displayName: string;
};

type PrivateReadingSummary = {
  id: string;
  profileId: string;
  title: string;
  privacyAcceptedAt: string;
  privacyNoticeVersion: string;
  createdAt: string;
  updatedAt: string;
  preview: string;
  wordCount: number;
};

type PrivateReadingRecord = PrivateReadingSummary & {
  sourceText: string;
};

function calmLearningDate(value?: string) {
  if (!value) return "This session";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Date preserved";
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function contextSourceName(context: VocabularyContext, privateReadings: PrivateReadingSummary[]) {
  if (context.sourceType === "private_import") {
    return privateReadings.find((reading) => reading.id === context.sourceId)?.title || "Private passage";
  }
  return READING_BY_ID.get(context.sourceId)?.title || "Verbalore reading";
}

function evidenceReceipt(review?: WordReview) {
  if (!review) return "Saved in context. No learning assessment has been made.";
  const { evidence } = review;
  if (evidence.status === "Owned") {
    return `${evidence.successfulChecks} checked recalls, a learner-written use, and ${evidence.naturalUses} self-reported natural ${evidence.naturalUses === 1 ? "use" : "uses"}.`;
  }
  if (evidence.status === "Active") {
    return `${evidence.successfulChecks} checked ${evidence.successfulChecks === 1 ? "recall" : "recalls"} and ${evidence.useAttempts} learner-written ${evidence.useAttempts === 1 ? "use" : "uses"}.`;
  }
  return evidence.successfulChecks
    ? `${evidence.successfulChecks} checked ${evidence.successfulChecks === 1 ? "recall" : "recalls"}; more evidence is still required.`
    : "Saved in context. Recognition is recorded, but learning has not been claimed.";
}

const DEFAULT_PROFILE: Profile = {
  id: "guest",
  displayName: "Reader",
  preferredLanguage: "English",
  explorationDepth: "Glance first",
  assistanceMode: "Build with me",
};

const WORD_PATTERN = /^\p{L}+(?:[’'-]\p{L}+)*$/u;
function pronunciationLabel(voice: SpeechSynthesisVoice) {
  const accent = new Intl.DisplayNames(["en"], { type: "region" });
  const region = voice.lang.split("-")[1]?.toUpperCase();
  const regionName = region ? accent.of(region) : undefined;
  return `${regionName || voice.lang} · ${voice.name}${voice.localService ? " · device" : ""}`;
}

function normalizeWord(value: string) {
  return value.toLocaleLowerCase().replace(/^[^\p{L}]+|[^\p{L}]+$/gu, "");
}

function displayLexicalText(value: string) {
  return value.replace(/\s+—\s+/g, "; ").replaceAll("—", ", ");
}

function vocabularyStatusRank(status: string) {
  return status === "Owned" ? 2 : status === "Active" ? 1 : 0;
}

function reviewStepName(step: ReviewEvidence["nextStep"]) {
  if (step === "recall") return "Recall it";
  if (step === "contrast") return "Distinguish it";
  if (step === "use") return "Use it";
  return "Notice it in life";
}

function calmReviewDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "when you are ready";
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function fallbackInsight(selection: SelectionState): WordInsight {
  return {
    word: selection.word,
    phonetic: "Selected from the passage",
    partOfSpeech: selection.word.includes(" ") ? "phrase" : "word",
    meaning:
      "No local lexical entry is available for this selection. Its sentence remains preserved, and nothing was sent outside Verbalore.",
    synonym: "Not available",
    antonym: "Not available",
    translations: {
      English: "No local entry",
      Hindi: "स्थानीय प्रविष्टि उपलब्ध नहीं है",
      Punjabi: "ਸਥਾਨਕ ਐਂਟਰੀ ਉਪਲਬਧ ਨਹੀਂ ਹੈ",
    },
    shade: "Verbalore records the boundary honestly when its maintained local vocabulary does not cover a private selection.",
    whyHere: "The surrounding sentence remains available, but Verbalore does not invent a definition from context.",
    intonation: "No context-specific guidance has been authored for this selection.",
    family: "No maintained local entry",
    example: selection.sentence,
    sentence: selection.sentence,
  };
}

function dictionaryEntryToInsight(selection: SelectionState, entry: LessonLexiconEntry): WordInsight {
  const sense = entry.senses[0];
  const pronunciation = entry.pronunciations[0];
  const nearestWord = sense.synonyms[0] || entry.lemma;
  return {
    word: selection.word,
    phonetic: pronunciation
      ? `/${pronunciation.value}/${pronunciation.variety ? ` · ${pronunciation.variety}` : ""}`
      : `lemma · ${entry.lemma}`,
    partOfSpeech: sense.partOfSpeech,
    meaning: displayLexicalText(sense.definition),
    synonym: nearestWord,
    antonym: "Not supplied by this local entry",
    translations: {
      English: sense.definition,
      Hindi: "Translation is available for editorially taught words.",
      Punjabi: "Translation is available for editorially taught words.",
    },
    shade: "A maintained dictionary definition, not a sentence-specific interpretation.",
    whyHere: "Verbalore has not claimed which listed sense the author intended in this sentence.",
    intonation: "Context-specific voice guidance is reserved for editorially taught words.",
    family: `Dictionary lemma · ${entry.lemma}`,
    example: sense.examples[0] ? displayLexicalText(sense.examples[0]) : selection.sentence,
    sentence: selection.sentence,
  };
}

function privateReadingToReading(record: PrivateReadingRecord): Reading {
  const paragraphs = record.sourceText
    .split(/\n\s*\n/g)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
  return {
    id: `private:${record.id}`,
    edition: record.updatedAt.slice(0, 10) + ".1",
    title: record.title,
    subtitle: "Private reading · visible only in your account",
    deck: "Read at your pace. Nothing in this passage is analysed automatically.",
    whyItFits: "You brought this passage because its language matters to you.",
    provenance: "Private import",
    taxonomyVersion: "taxonomy_v1",
    taxonomy: {
      outcomes: ["learner-directed reading"],
      rhetoricalMoves: [],
      lexicalChallenge: "advanced",
      syntax: [],
      register: [],
      stance: [],
      form: "essay",
      mood: [],
      readingMinutes: Math.max(1, Math.round(record.wordCount / 210)),
    },
    segments: (paragraphs.length ? paragraphs : [record.sourceText]).map((text, index) => ({
      id: `p${index}`,
      kind: "prose",
      text,
    })),
    words: {},
    exercise: {
      kicker: "Private passage",
      title: "Keep only what earns a place.",
      prompt: "Select a word when it asks for attention; continue reading when it does not.",
      targetWords: [],
    },
  };
}

function evaluateBoundaryPractice(value: string): PracticeAssessment {
  const text = value.trim();
  const tokens = text.match(/\p{L}+(?:[’'-]\p{L}+)*/gu) || [];
  const words = tokens.length;
  const hasFirstPerson = /\b(?:i|me|my|mine)\b/i.test(text);
  const hasTurnAction = /\b(?:finish|complete|continue|speak|point|thought|turn|floor|respond|response|hear|listen|interrupt|wait)\b/i.test(text);
  const hasBoundary = /\b(?:let me|i(?:['’]ll| will)|i need|i want|finish|complete)\b/i.test(text);
  const hasHedges = /\b(?:just|maybe|perhaps|wondering|sort of|kind of|sorry)\b|if (?:that(?:’|')s|this is) okay/i.test(text);
  const hasReciprocity = /\b(?:then|after|hear|listen|respond|response|your turn|the floor|please)\b/i.test(text);

  const signals = [
    {
      id: "boundary",
      label: "The boundary is stated",
      met: hasBoundary,
      guidance: hasBoundary
        ? "The sentence says what will happen instead of asking whether it may happen."
        : "Name the action directly: “Let me finish…” or “I’ll complete the point…”",
    },
    {
      id: "hedges",
      label: "Permission-seeking is removed",
      met: Boolean(text) && !hasHedges,
      guidance: hasHedges
        ? "Remove softeners such as just, maybe, wondering, and if that’s okay."
        : "Courtesy remains possible without shrinking the request.",
    },
    {
      id: "reciprocity",
      label: "The other person keeps their dignity",
      met: hasReciprocity,
      guidance: hasReciprocity
        ? "You have made room for the other person after protecting your own turn."
        : "Consider a second clause: “then I want to hear your response.”",
    },
    {
      id: "economy",
      label: "The sentence carries its own weight",
      met: words >= 5 && words <= 22,
      guidance:
        words > 22
          ? "Compress the line. A boundary weakens when it must survive too much explanation."
          : words > 0
            ? "Aim for roughly five to twenty-two words: enough for clarity, little enough for force."
            : "Write the boundary in your own words before comparing it with the models.",
    },
  ];

  if (!text) {
    return {
      status: "empty",
      message: "No sentence was assessed. The examples below are authored demonstrations.",
      signals,
    };
  }

  if (words < 3) {
    return {
      status: "not-assessed",
      message: "That is too short for this check. Write the complete line you would actually say.",
      signals,
    };
  }

  if (!hasFirstPerson || !hasTurnAction) {
    return {
      status: "not-assessed",
      message: "This check is built for a spoken boundary. State your action: finish, continue, or speak. Make the order of turns clear.",
      signals,
    };
  }

  return {
    status: "assessed",
    message: "This authored check notices visible sentence patterns. It does not judge meaning, intention, or social context.",
    signals,
  };
}

const EXPRESSION_PATTERN_PROFILES: Record<string, { intention: RegExp; invariant: RegExp; contradiction?: RegExp }> = {
  "disagree-without-a-fight": {
    intention: /\b(?:disagree|don['’]t agree|not convinced|push back|not comfortable|against it|wrong call)\b/i,
    invariant: /\b(?:why|because|show|better|appeal|team|group|still|go with)\b/i,
  },
  "a-no-that-stays-no": {
    intention: /\b(?:no|can['’]t|cannot|won['’]t|will not|not able|not mine to carry)\b/i,
    invariant: /\b(?:no|can['’]t|cannot|won['’]t|will not|not able|not mine to carry|decision is made|not open)\b/i,
    contradiction: /\b(?:maybe|tomorrow|later|for now|this time|ask (?:me )?again|another time)\b/i,
  },
  "correct-dont-crush": {
    intention: /\b(?:error|mistake|wrong|off|doesn['’]t add up|needs? another pass|need to fix|needs? fixing)\b/i,
    invariant: /\b(?:fix|check|look|pass|before|send|ship|goes out)\b/i,
  },
  "say-what-you-want": {
    intention: /\b(?:can you|could you|would you|are you free|i['’]d like you|you in)\b/i,
    invariant: /\b(?:friday|session|cover|take)\b/i,
  },
  "the-truth-whole": {
    intention: /\b(?:isn['’]t working|not working|not going to work|don['’]t think .* work|over|finished|don['’]t see .* turning around)\b/i,
    invariant: /\b(?:more time|won['’]t change|will not change|not going to change|turning around|over|finished|ending|end it)\b/i,
  },
  "the-exact-feeling": {
    intention: /\b(?:hurt|left out|overlooked|stung|disappointed|dismissed|excluded|betrayed|uneasy)\b/i,
    invariant: /\b(?:because|that|when|you|not)\b/i,
  },
  "move-them-dont-push": {
    intention: /\b(?:back (?:this|a)|pilot|hear me out|give this|consider|worth your time|make the case)\b/i,
    invariant: /\b(?:two weeks?|risk|evidence|decide|commitment)\b/i,
  },
  "sorry-not-smaller": {
    intention: /\b(?:my fault|my mistake|i was wrong|i got .* wrong|this was on me|i own)\b/i,
    invariant: /\b(?:sorry|fix|make it right|cost you|put you through|own)\b/i,
  },
  "cut-to-the-point": {
    intention: /\bthursday\b/i,
    invariant: /\b(?:no review|review['’]s cancelled|review is cancelled|skipping the review)\b/i,
  },
  "on-the-record": {
    intention: /\b(?:disagree|against it|wrong call|not comfortable|silence read as agreement)\b/i,
    invariant: /\b(?:record|team|group|go with|cooperate|respect where)\b/i,
  },
  "light-and-still-standing": {
    intention: /\b(?:review|step|rework|later|rescue|rescues)\b/i,
    invariant: /\b(?:prevents?|saves?|avoid|more work|rework|rescue|rescues|later)\b/i,
  },
};

function evaluatePractice(value: string, study: ExpressionStudy): PracticeAssessment {
  if (study.id === BOUNDARY_STUDY.id) return evaluateBoundaryPractice(value);

  const text = value.trim();
  const tokens = text.match(/\p{L}+(?:[’'-]\p{L}+)*/gu) || [];
  const words = tokens.length;
  const pattern = EXPRESSION_PATTERN_PROFILES[study.id];
  const intentionVisible = Boolean(text && pattern?.intention.test(text));
  const invariantVisible = Boolean(
    text
    && intentionVisible
    && pattern?.invariant.test(text)
    && !pattern?.contradiction?.test(text),
  );
  const hasObviousPersonalAttack = /\b(?:idiot|moron|fool|bully|stupid|selfish|useless|pathetic|incompetent|hopeless|dishonest|lazy|hate you|shut up)\b/i.test(text);
  const upperBound = study.size === "quick" ? 22 : study.size === "standard" ? 34 : 46;

  const signals: PracticeSignal[] = [
    {
      id: "intention",
      label: "The intention is visible",
      met: intentionVisible,
      guidance: intentionVisible
        ? `The line contains the visible move this ${study.skill.toLocaleLowerCase()} study is built to practise.`
        : `State the move plainly: ${study.intention}`,
    },
    {
      id: "invariant",
      label: "The invariant survives",
      met: invariantVisible,
      guidance: invariantVisible
        ? "The authored facts this study must preserve are still available in the sentence."
        : `Check the sentence against this contract: ${study.invariant}`,
    },
    {
      id: "dignity",
      label: "No obvious personal attack is visible",
      met: Boolean(text) && !hasObviousPersonalAttack,
      guidance: hasObviousPersonalAttack
        ? "Name the problem, decision, or feeling without reducing the other person to an insult."
        : "This limited check found no obvious insult. Dignity still depends on context and delivery.",
    },
    {
      id: "economy",
      label: "The sentence is proportionate",
      met: words >= 3 && words <= upperBound,
      guidance: words > upperBound
        ? `This ${study.size} study asks for no more than ${upperBound} words. Cut explanation that does not protect the intention.`
        : "The sentence has enough room for clarity without making the listener excavate the point.",
    },
  ];

  if (!text) {
    return {
      status: "empty",
      message: "No sentence was assessed. The articulations above remain authored demonstrations.",
      signals,
    };
  }

  if (words < 3 || !intentionVisible) {
    return {
      status: "not-assessed",
      message: `This narrow authored check could not verify the visible move for “${study.title}”. It shows no score rather than guessing at your meaning.`,
      signals,
    };
  }

  return {
    status: "assessed",
    message: "This authored check notices only visible wording patterns. It does not judge your intention, relationship, delivery, or whether this stance is best for the moment.",
    signals,
  };
}

function StoryText({ segment, words }: { segment: StorySegment; words: Record<string, WordInsight> }) {
  const parts = segment.text.split(/(\p{L}+(?:[’'-]\p{L}+)*)/gu);
  return (
    <>
      {parts.map((part, index) => {
        if (!WORD_PATTERN.test(part)) return part;
        const key = normalizeWord(part);
        const tokenId = `${segment.id}-t${index}`;
        const insight = words[key];
        const shared = {
          "data-selectable-word": "true",
          "data-word-key": key,
          "data-display-word": part,
          "data-token-id": tokenId,
          "data-paragraph-id": segment.id,
          "data-sentence": insight?.sentence || segment.text,
        };

        if (insight) {
          return (
            <span
              key={tokenId}
              className="selectable-word enriched-word"
              role="button"
              tabIndex={0}
              aria-label={`Explore ${part}`}
              {...shared}
            >
              {part}
            </span>
          );
        }

        return (
          <span key={tokenId} className="selectable-word" tabIndex={-1} {...shared}>
            {part}
          </span>
        );
      })}
    </>
  );
}

export default function Home() {
  const storyRef = useRef<HTMLElement>(null);
  const inspectorRef = useRef<HTMLElement>(null);
  const pointerStart = useRef({ x: 0, y: 0, at: 0, pointerType: "mouse" });
  const pendingTap = useRef<number | null>(null);
  const lastTouchRelease = useRef<{ at: number; tokenId: string }>({ at: 0, tokenId: "" });
  const wordOrigin = useRef({ x: 0, y: 0 });
  const progressReady = useRef(false);
  const readingSizeMenuRef = useRef<HTMLDetailsElement>(null);
  const readingSizeAnchorRef = useRef<{ element: HTMLElement; top: number } | null>(null);
  const [showHome, setShowHome] = useState(true);
  const [currentReadingId, setCurrentReadingId] = useState(() => {
    if (typeof window === "undefined") return READINGS[0].id;
    const remembered = window.localStorage.getItem("verbalore-current-reading");
    return remembered && (READING_BY_ID.has(remembered) || /^private:[a-f0-9-]{36}$/i.test(remembered))
      ? remembered
      : READINGS[0].id;
  });
  const [lessonLexiconState, setLessonLexiconState] = useState<{
    readingId: string;
    lexicon: LessonLexicon | null;
  }>(() => currentReadingId === READINGS[0].id
    ? { readingId: READINGS[0].id, lexicon: INITIAL_LESSON_LEXICON }
    : { readingId: currentReadingId, lexicon: null });
  const [discoveryOpen, setDiscoveryOpen] = useState(false);
  const [readingIntent, setReadingIntent] = useState<ReadingIntent>("tactful-confidence");
  const [profile, setProfile] = useState<Profile>(DEFAULT_PROFILE);
  const [savedWords, setSavedWords] = useState<VocabularyItem[]>([]);
  const [savedContexts, setSavedContexts] = useState<VocabularyContext[]>([]);
  const [privateReadings, setPrivateReadings] = useState<PrivateReadingSummary[]>([]);
  const [privateReadingRecords, setPrivateReadingRecords] = useState<Record<string, PrivateReadingRecord>>({});
  const [importOpen, setImportOpen] = useState(false);
  const [importTitle, setImportTitle] = useState("");
  const [importSource, setImportSource] = useState("");
  const [importAccepted, setImportAccepted] = useState(false);
  const [importError, setImportError] = useState("");
  const [savingImport, setSavingImport] = useState(false);
  const [confirmDeleteImport, setConfirmDeleteImport] = useState<string | null>(null);
  const [selection, setSelection] = useState<SelectionState | null>(null);
  const [pendingLibraryAnchor, setPendingLibraryAnchor] = useState<VocabularyItem | null>(null);
  const [wordMotion, setWordMotion] = useState<"open" | "replace">("replace");
  const [panelMode, setPanelMode] = useState<PanelMode>("empty");
  const [deepOpen, setDeepOpen] = useState(false);
  const [currentParagraph, setCurrentParagraph] = useState("p0");
  const [progress, setProgress] = useState(0);
  const [resumeParagraph, setResumeParagraph] = useState<string | null>(null);
  const [readingProgresses, setReadingProgresses] = useState<Record<string, ReadingProgress>>({});
  const [showReturn, setShowReturn] = useState(false);
  const [resumeCue, setResumeCue] = useState<{
    tokenId: string;
    paragraphId: string;
    word: string;
  } | null>(null);
  const [meaningView, setMeaningView] = useState(0);
  const [toast, setToast] = useState("");
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [experienceMode, setExperienceMode] = useState<ExperienceMode>("read");
  const [activeExpressionStudyId, setActiveExpressionStudyId] = useState(() => {
    if (typeof window === "undefined") return BOUNDARY_STUDY.id;
    const remembered = window.localStorage.getItem("verbalore-expression-study");
    return remembered && EXPRESSION_STUDY_BY_ID.has(remembered) ? remembered : BOUNDARY_STUDY.id;
  });
  const [expressionWork, setExpressionWork] = useState<Record<string, ExpressionWork>>({});
  const [practiceDraft, setPracticeDraft] = useState("");
  const [practiceRevision, setPracticeRevision] = useState("");
  const [practiceChecked, setPracticeChecked] = useState(false);
  const [activeStanceId, setActiveStanceId] = useState(() => {
    if (typeof window === "undefined") return "tactful";
    const remembered = window.localStorage.getItem("verbalore-expression-stance");
    return BOUNDARY_STUDY.stances.some((stance) => stance.id === remembered) ? remembered! : "tactful";
  });
  const [practiceAttempts, setPracticeAttempts] = useState<PracticeAttempt[]>([]);
  const [savingPractice, setSavingPractice] = useState(false);
  const [practiceView, setPracticeView] = useState<PracticeView>("words");
  const [wordReviews, setWordReviews] = useState<WordReview[]>([]);
  const [reviewWord, setReviewWord] = useState<string | null>(null);
  const [reviewChoice, setReviewChoice] = useState("");
  const [reviewDraft, setReviewDraft] = useState("");
  const [reviewSignals, setReviewSignals] = useState<string[]>([]);
  const [reviewFeedback, setReviewFeedback] = useState("");
  const [savingReview, setSavingReview] = useState(false);
  const [currentUser, setCurrentUser] = useState<SessionUser | null>(null);
  const [googleClientId, setGoogleClientId] = useState<string | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [signInOpen, setSignInOpen] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  const [authError, setAuthError] = useState("");
  const [speechVoices, setSpeechVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [speechVoiceUri, setSpeechVoiceUri] = useState(() =>
    typeof window === "undefined"
      ? "default"
      : window.localStorage.getItem("verbalore-pronunciation-voice") || "default",
  );
  const [readingSize, setReadingSize] = useState<ReadingSize>(() => {
    if (typeof window === "undefined") return "balanced";
    const remembered = window.localStorage.getItem("verbalore-reading-size");
    return remembered === "compact" || remembered === "generous" ? remembered : "balanced";
  });
  const [theme, setTheme] = useState<"paper" | "night">(() => {
    if (typeof window === "undefined") return "paper";
    const remembered =
      window.localStorage.getItem("verbalore-theme") ||
      window.localStorage.getItem("weight-of-words-theme");
    return remembered === "night" ? "night" : "paper";
  });

  const activePrivateId = currentReadingId.startsWith("private:")
    ? currentReadingId.slice("private:".length)
    : null;
  const reading = useMemo(() => {
    if (activePrivateId && privateReadingRecords[activePrivateId]) {
      return privateReadingToReading(privateReadingRecords[activePrivateId]);
    }
    return READING_BY_ID.get(currentReadingId) || READINGS[0];
  }, [activePrivateId, currentReadingId, privateReadingRecords]);
  const isPrivateReading = reading.provenance === "Private import";
  const activeLessonLexicon = lessonLexiconState.readingId === reading.id
    ? lessonLexiconState.lexicon
    : null;
  const story = reading.segments;
  const words = reading.words;
  const storyTokenIds = useMemo(() => readingTokenIds(reading), [reading]);
  const readingRecommendations = useMemo(
    () => recommendReadings(readingIntent, reading.id),
    [reading.id, readingIntent],
  );

  useEffect(() => {
    let current = true;
    if (isPrivateReading) {
      return () => { current = false; };
    }
    if (reading.id === READINGS[0].id) {
      return () => { current = false; };
    }
    void loadLessonLexicon(reading.id).then((lexicon) => {
      if (current) setLessonLexiconState({ readingId: reading.id, lexicon });
    });
    return () => { current = false; };
  }, [isPrivateReading, reading.id]);

  const privateBundledInsight = useMemo(
    () => isPrivateReading && selection ? findWordInsight(selection.key) : undefined,
    [isPrivateReading, selection],
  );
  const contextualInsight = useMemo(
    () => (selection ? words[selection.key] : undefined),
    [selection, words],
  );
  const dictionaryEntry = useMemo(
    () => (selection ? findDictionaryEntry(selection.key, activeLessonLexicon) : undefined),
    [activeLessonLexicon, selection],
  );
  const dictionaryOnly = Boolean(selection && dictionaryEntry && !contextualInsight);
  const noLexicalEntry = Boolean(
    selection && !contextualInsight && !dictionaryEntry && !privateBundledInsight,
  );
  const insight = useMemo(
    () => (selection
      ? contextualInsight || (dictionaryEntry ? dictionaryEntryToInsight(selection, dictionaryEntry) : undefined) || privateBundledInsight || fallbackInsight(selection)
      : null),
    [contextualInsight, dictionaryEntry, privateBundledInsight, selection],
  );
  const meaningLabel = noLexicalEntry
    ? "No local entry"
    : dictionaryOnly
      ? dictionaryEntry?.senseStatus === "grammar"
        ? "Grammar note"
        : dictionaryEntry?.senseStatus === "entity"
          ? "In this reading"
          : "Dictionary meaning"
      : isPrivateReading
        ? "Maintained meaning · sense unverified"
        : "Meaning here";
  const meaningViews = useMemo(() => {
    if (!insight) return [];
    const candidates: Array<{ label: string; text: string; authored: boolean }> = [
      { label: meaningLabel, text: insight.meaning, authored: !dictionaryOnly && !noLexicalEntry },
    ];
    if (contextualInsight && !isPrivateReading) {
      candidates.push(
        { label: "Another way to understand it", text: insight.shade, authored: true },
        { label: "Why it fits this sentence", text: insight.whyHere, authored: true },
      );
    }
    if (dictionaryEntry?.senseStatus === "dictionary") {
      dictionaryEntry.senses.slice(0, 4).forEach((sense, index) => {
        candidates.push({
          label: `Dictionary sense ${index + 1}`,
          text: displayLexicalText(sense.definition),
          authored: false,
        });
      });
    }
    return candidates.filter((candidate, index, all) =>
      candidate.text.trim() && all.findIndex((item) => item.text.trim().toLocaleLowerCase() === candidate.text.trim().toLocaleLowerCase()) === index
    );
  }, [contextualInsight, dictionaryEntry, dictionaryOnly, insight, isPrivateReading, meaningLabel, noLexicalEntry]);
  const activeMeaning = meaningViews[Math.min(meaningView, Math.max(0, meaningViews.length - 1))];
  const contextSentence = selection && insight
    ? isPrivateReading ? selection.sentence : insight.sentence
    : "";
  const savedKeys = useMemo(
    () => new Set(savedWords.map((item) => item.word)),
    [savedWords],
  );
  const contextsByWord = useMemo(() => {
    const grouped = new Map<string, VocabularyContext[]>();
    for (const context of savedContexts) {
      const current = grouped.get(context.word) || [];
      current.push(context);
      grouped.set(context.word, current);
    }
    return grouped;
  }, [savedContexts]);
  const reviewsByWord = useMemo(
    () => new Map(wordReviews.map((review) => [review.item.word, review])),
    [wordReviews],
  );
  const isSaved = selection ? savedKeys.has(selection.key) : false;
  const practiceText = practiceRevision.trim() || practiceDraft.trim();
  const expressionStudy = useMemo(
    () => EXPRESSION_STUDY_BY_ID.get(activeExpressionStudyId) || BOUNDARY_STUDY,
    [activeExpressionStudyId],
  );
  const expressionStudyIndex = ALL_EXPRESSION_STUDIES.findIndex((study) => study.id === expressionStudy.id);
  const expressionTitleParts = expressionStudy.title.split(", ");
  const practiceAssessment = useMemo(
    () => evaluatePractice(practiceText, expressionStudy),
    [expressionStudy, practiceText],
  );
  const practiceSignals = practiceAssessment.signals;
  const activeStance = useMemo(
    () => expressionStudy.stances.find((stance) => stance.id === activeStanceId) || expressionStudy.stances[0],
    [activeStanceId, expressionStudy],
  );

  const actionableReviews = useMemo(
    () => wordReviews.filter((review) => review.prompt),
    [wordReviews],
  );
  const currentReview = useMemo(
    () =>
      actionableReviews.find((review) => review.item.word === reviewWord) ||
      actionableReviews[0] ||
      null,
    [actionableReviews, reviewWord],
  );
  const dueReviewCount = useMemo(
    () => actionableReviews.filter((review) => review.evidence.due).length,
    [actionableReviews],
  );
  const adjacentTokenIds = useMemo(() => {
    if (!selection || selection.tokenId.startsWith("range-")) {
      return { previous: null, next: null };
    }
    const index = storyTokenIds.indexOf(selection.tokenId);
    return {
      previous: index > 0 ? storyTokenIds[index - 1] : null,
      next: index >= 0 && index < storyTokenIds.length - 1 ? storyTokenIds[index + 1] : null,
    };
  }, [selection, storyTokenIds]);

  const notify = useCallback((message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 2600);
  }, []);

  const openReading = useCallback(async (readingId: string) => {
    if (!READING_BY_ID.has(readingId)) return false;
    const lexicon = await loadLessonLexicon(readingId);
    if (!lexicon) {
      notify("That reading pack could not be opened. Your current place is unchanged.");
      return false;
    }
    setLessonLexiconState({ readingId, lexicon });
    setCurrentReadingId(readingId);
    window.localStorage.setItem("verbalore-current-reading", readingId);
    setExperienceMode("read");
    setPanelMode("empty");
    setSelection(null);
    setDeepOpen(false);
    setDiscoveryOpen(false);
    setImportOpen(false);
    const savedProgress = readingProgresses[readingId];
    setCurrentParagraph(savedProgress?.paragraphId || "p0");
    setProgress(savedProgress?.percentage || 0);
    setResumeParagraph(savedProgress?.paragraphId || null);
    window.setTimeout(() => document.getElementById("lesson-start")?.scrollIntoView({ block: "start" }), 0);
    return true;
  }, [notify, readingProgresses]);

  const activatePrivateReading = useCallback((record: PrivateReadingRecord) => {
    const readingId = `private:${record.id}`;
    setPrivateReadingRecords((current) => ({ ...current, [record.id]: record }));
    setCurrentReadingId(readingId);
    window.localStorage.setItem("verbalore-current-reading", readingId);
    setExperienceMode("read");
    setPanelMode("empty");
    setSelection(null);
    setDeepOpen(false);
    setDiscoveryOpen(false);
    setImportOpen(false);
    const savedProgress = readingProgresses[readingId];
    setCurrentParagraph(savedProgress?.paragraphId || "p0");
    setProgress(savedProgress?.percentage || 0);
    setResumeParagraph(savedProgress?.paragraphId || null);
    window.setTimeout(() => document.getElementById("lesson-start")?.scrollIntoView({ block: "start" }), 0);
  }, [readingProgresses]);

  const openPrivateReading = useCallback(async (id: string) => {
    const cached = privateReadingRecords[id];
    if (cached) {
      activatePrivateReading(cached);
      return cached;
    }
    try {
      const response = await fetch(`/api/imports?id=${encodeURIComponent(id)}`);
      const data = await response.json();
      if (!response.ok || !data.reading) {
        throw new Error(data.error || "Private reading could not be opened.");
      }
      activatePrivateReading(data.reading);
      return data.reading as PrivateReadingRecord;
    } catch (error) {
      notify(error instanceof Error ? error.message : "Private reading could not be opened.");
      return null;
    }
  }, [activatePrivateReading, notify, privateReadingRecords]);

  const loadLearnerData = useCallback(async () => {
    setLoadingProfile(true);
    progressReady.current = false;
    try {
      const [profileResponse, practiceResponse, reviewResponse, importsResponse] = await Promise.all([
        fetch("/api/profile"),
        fetch("/api/practice"),
        fetch("/api/review"),
        fetch("/api/imports"),
      ]);
      if (!profileResponse.ok || !practiceResponse.ok || !reviewResponse.ok || !importsResponse.ok) {
        throw new Error("Private profile unavailable");
      }
      const [profileData, practiceData, reviewData, importsData] = await Promise.all([
        profileResponse.json(),
        practiceResponse.json(),
        reviewResponse.json(),
        importsResponse.json(),
      ]);
      if (profileData.profile) setProfile(profileData.profile);
      if (profileData.vocabulary) setSavedWords(profileData.vocabulary);
      if (profileData.contexts) setSavedContexts(profileData.contexts);
      if (profileData.progresses) {
        const byReading = Object.fromEntries(
          (profileData.progresses as ReadingProgress[]).map((item) => [item.lessonId, item]),
        );
        setReadingProgresses(byReading);
        const rememberedReading = window.localStorage.getItem("verbalore-current-reading") || READINGS[0].id;
        const current = byReading[rememberedReading];
        if (current) {
          setResumeParagraph(current.paragraphId);
          setProgress(current.percentage || 0);
        }
      } else if (profileData.progress?.lessonId === (window.localStorage.getItem("verbalore-current-reading") || READINGS[0].id)) {
        setResumeParagraph(profileData.progress.paragraphId);
        setProgress(profileData.progress.percentage || 0);
      }
      if (practiceData.attempts) setPracticeAttempts(practiceData.attempts);
      if (reviewData.reviews) {
        setWordReviews(reviewData.reviews);
        setReviewWord((current) => current || reviewData.reviews[0]?.item?.word || null);
      }
      const imported = (importsData.readings || []) as PrivateReadingSummary[];
      setPrivateReadings(imported);
      const rememberedReading = window.localStorage.getItem("verbalore-current-reading") || READINGS[0].id;
      const rememberedPrivateId = rememberedReading.startsWith("private:")
        ? rememberedReading.slice("private:".length)
        : null;
      if (rememberedPrivateId && imported.some((item) => item.id === rememberedPrivateId)) {
        const sourceResponse = await fetch(`/api/imports?id=${encodeURIComponent(rememberedPrivateId)}`);
        if (sourceResponse.ok) {
          const sourceData = await sourceResponse.json();
          if (sourceData.reading) {
            setPrivateReadingRecords((current) => ({
              ...current,
              [rememberedPrivateId]: sourceData.reading,
            }));
            setCurrentReadingId(rememberedReading);
          }
        }
      } else if (rememberedPrivateId) {
        setCurrentReadingId(READINGS[0].id);
        window.localStorage.setItem("verbalore-current-reading", READINGS[0].id);
      }
      window.setTimeout(() => {
        progressReady.current = true;
      }, 500);
    } catch {
      notify("Your account opened, but its learning data could not be loaded yet.");
    } finally {
      setLoadingProfile(false);
    }
  }, [notify]);

  useEffect(() => {
    const syncViewFromLocation = () => setShowHome(window.location.hash !== "#read" && window.location.hash !== "#practice");
    syncViewFromLocation();
    window.addEventListener("popstate", syncViewFromLocation);
    window.addEventListener("hashchange", syncViewFromLocation);
    return () => {
      window.removeEventListener("popstate", syncViewFromLocation);
      window.removeEventListener("hashchange", syncViewFromLocation);
    };
  }, []);

  useEffect(() => {
    const rememberedTheme =
      window.localStorage.getItem("verbalore-theme") ||
      window.localStorage.getItem("weight-of-words-theme");
    if (rememberedTheme === "night") {
      document.documentElement.dataset.theme = "night";
    }

    let cancelled = false;
    Promise.all([fetch("/api/auth/config"), fetch("/api/auth/session")])
      .then(async ([configResponse, sessionResponse]) => {
        const [configData, sessionData] = await Promise.all([
          configResponse.json(),
          sessionResponse.json(),
        ]);
        if (cancelled) return;
        setGoogleClientId(configData.googleClientId || null);
        setAuthReady(true);
        if (sessionData.user) {
          setCurrentUser(sessionData.user);
          await loadLearnerData();
        } else {
          setLoadingProfile(false);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setAuthReady(true);
        setLoadingProfile(false);
      });
    return () => {
      cancelled = true;
    };
  }, [loadLearnerData]);

  useEffect(() => {
    const openChooser = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (event.key !== "/" || target?.matches("input, textarea, select, [contenteditable='true']")) return;
      event.preventDefault();
      setDiscoveryOpen(true);
    };
    window.addEventListener("keydown", openChooser);
    return () => window.removeEventListener("keydown", openChooser);
  }, []);

  useEffect(() => {
    if (!("speechSynthesis" in window)) return;
    const loadVoices = () => {
      const englishVoices = window.speechSynthesis
        .getVoices()
        .filter((voice) => voice.lang.toLowerCase().startsWith("en"))
        .sort((a, b) => a.lang.localeCompare(b.lang) || a.name.localeCompare(b.name));
      setSpeechVoices(englishVoices);
    };
    loadVoices();
    window.speechSynthesis.addEventListener("voiceschanged", loadVoices);
    return () => window.speechSynthesis.removeEventListener("voiceschanged", loadVoices);
  }, []);

  useEffect(() => {
    if (showHome) return;
    const paragraphs = Array.from(
      document.querySelectorAll<HTMLElement>("[data-reading-paragraph]"),
    );
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!visible) return;
        const id = (visible.target as HTMLElement).dataset.paragraphId || "p0";
        const index = story.findIndex((segment) => segment.id === id);
        setCurrentParagraph(id);
        setProgress(Math.round(((index + 1) / story.length) * 100));
      },
      { rootMargin: "-25% 0px -58% 0px", threshold: [0.05, 0.4, 0.8] },
    );
    paragraphs.forEach((paragraph) => observer.observe(paragraph));
    return () => observer.disconnect();
  }, [reading.id, showHome, story]);

  useEffect(() => {
    if (!currentUser || !progressReady.current) return;
    const timer = window.setTimeout(() => {
      void fetch("/api/progress", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          lessonId: reading.id,
          paragraphId: currentParagraph,
          percentage: progress,
          selectedWord: selection?.word || null,
          selectedAnchor: selection?.paragraphId || null,
        }),
      });
    }, 650);
    return () => window.clearTimeout(timer);
  }, [currentParagraph, currentUser, progress, reading.id, selection]);

  useEffect(() => {
    window.localStorage.setItem("verbalore-expression-stance", activeStanceId);
  }, [activeStanceId]);

  useLayoutEffect(() => {
    const anchor = readingSizeAnchorRef.current;
    if (!anchor) return;
    const difference = anchor.element.getBoundingClientRect().top - anchor.top;
    if (Math.abs(difference) > 0.5) window.scrollBy(0, difference);
    readingSizeAnchorRef.current = null;
  }, [readingSize]);

  useLayoutEffect(() => {
    let scheduled = false;
    const checkAnchor = () => {
      scheduled = false;
      if (!selection) {
        setShowReturn(false);
        return;
      }
      const target = selection.tokenId.startsWith("range-")
        ? document.querySelector<HTMLElement>(
            `[data-reading-paragraph][data-paragraph-id="${selection.paragraphId}"]`,
          )
        : document.querySelector<HTMLElement>(
            `[data-token-id="${selection.tokenId}"]`,
          );
      if (!target) return;
      const rect = target.getBoundingClientRect();
      setShowReturn(rect.bottom < 76 || rect.top > window.innerHeight - 96);
    };
    const onScroll = () => {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(checkAnchor);
    };
    checkAnchor();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [selection]);

  useLayoutEffect(() => {
    if (!selection || wordMotion !== "open" || !inspectorRef.current) return;
    const panelRect = inspectorRef.current.getBoundingClientRect();
    inspectorRef.current.style.setProperty(
      "--word-origin-x",
      `${wordOrigin.current.x - panelRect.left}px`,
    );
    inspectorRef.current.style.setProperty(
      "--word-origin-y",
      `${wordOrigin.current.y - panelRect.top}px`,
    );
  }, [selection, wordMotion]);

  useLayoutEffect(() => {
    const wordSheetOpen = panelMode === "word" && window.matchMedia("(max-width: 1080px)").matches;
    if (!discoveryOpen && !signInOpen && !importOpen && !wordSheetOpen) return;
    const body = document.body;
    const scrollY = window.scrollY;
    const previous = {
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      overflowY: body.style.overflowY,
    };
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";
    body.style.overflowY = "scroll";
    return () => {
      body.style.position = previous.position;
      body.style.top = previous.top;
      body.style.width = previous.width;
      body.style.overflowY = previous.overflowY;
      window.scrollTo(0, scrollY);
    };
  }, [discoveryOpen, importOpen, panelMode, signInOpen]);

  useEffect(() => () => {
    if (pendingTap.current !== null) window.clearTimeout(pendingTap.current);
    document
      .querySelectorAll(".tap-acknowledged")
      .forEach((element) => element.classList.remove("tap-acknowledged"));
  }, []);

  useEffect(() => {
    document
      .querySelectorAll(".current-selection")
      .forEach((element) => element.classList.remove("current-selection"));
    if (!selection || selection.tokenId.startsWith("range-")) return;
    const selectedElement = document.querySelector<HTMLElement>(
      `[data-token-id="${selection.tokenId}"]`,
    );
    selectedElement?.classList.add("current-selection");
    return () => selectedElement?.classList.remove("current-selection");
  }, [selection]);

  useEffect(() => {
    if (!resumeCue) return;
    const target = resumeCue.tokenId.startsWith("range-")
      ? document.querySelector<HTMLElement>(
          `[data-reading-paragraph][data-paragraph-id="${resumeCue.paragraphId}"]`,
        )
      : document.querySelector<HTMLElement>(
          `[data-token-id="${CSS.escape(resumeCue.tokenId)}"]`,
        );
    target?.classList.add("resume-location");
    const timer = window.setTimeout(() => {
      target?.classList.remove("resume-location");
      setResumeCue(null);
    }, 3600);
    return () => {
      window.clearTimeout(timer);
      target?.classList.remove("resume-location");
    };
  }, [resumeCue]);

  const selectElement = useCallback((element: HTMLElement) => {
    const word = element.dataset.displayWord || element.textContent || "";
    const key = element.dataset.wordKey || normalizeWord(word);
    const next: SelectionState = {
      word,
      key,
      tokenId: element.dataset.tokenId || `word-${Date.now()}`,
      paragraphId: element.dataset.paragraphId || "p0",
      sentence: element.dataset.sentence || element.closest("p")?.textContent || "",
      curated: Boolean(words[key]),
    };
    const rect = element.getBoundingClientRect();
    wordOrigin.current = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
    const revealSelection = () => {
      if (!document.contains(element)) return;
      setWordMotion(panelMode === "word" ? "replace" : "open");
      setMeaningView(0);
      setSelection(next);
      setPanelMode("word");
      setDeepOpen(profile.explorationDepth === "Open deeply");
      window.getSelection()?.removeAllRanges();
    };
    if (!isPrivateReading && !activeLessonLexicon) {
      void loadLessonLexicon(reading.id).then((lexicon) => {
        if (!lexicon) {
          notify("That word pack could not be opened. The reading remains in place.");
          return;
        }
        setLessonLexiconState({ readingId: reading.id, lexicon });
        revealSelection();
      });
      return;
    }
    revealSelection();
  }, [activeLessonLexicon, isPrivateReading, notify, panelMode, profile.explorationDepth, reading.id, words]);

  useEffect(() => {
    if (!pendingLibraryAnchor) return;
    const sourceAnchor = pendingLibraryAnchor.sourceAnchor;
    const isPrivateAnchor = sourceAnchor.startsWith("private:");
    const parts = sourceAnchor.split(":");
    const sourceReadingId = isPrivateAnchor ? `private:${parts[1] || ""}` : parts[0] || reading.id;
    const sourceParagraphId = isPrivateAnchor ? parts[2] || "p0" : parts.slice(1).join(":") || "p0";
    if (sourceReadingId !== reading.id) return;

    let secondFrame = 0;
    const firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => {
        const paragraph = document.querySelector<HTMLElement>(
          `[data-reading-paragraph][data-paragraph-id="${CSS.escape(sourceParagraphId)}"]`,
        );
        const element = paragraph?.querySelector<HTMLElement>(
          `[data-word-key="${CSS.escape(pendingLibraryAnchor.word)}"]`,
        );
        (element || paragraph)?.scrollIntoView({ block: "center", behavior: "auto" });
        if (element) {
          selectElement(element);
        } else {
          setWordMotion("open");
          setMeaningView(0);
          setSelection({
            word: pendingLibraryAnchor.displayWord,
            key: pendingLibraryAnchor.word,
            tokenId: `range-${Date.now()}`,
            paragraphId: sourceParagraphId,
            sentence: pendingLibraryAnchor.sourceSentence,
            curated: Boolean(findWordInsight(pendingLibraryAnchor.word)),
          });
          setPanelMode("word");
          setDeepOpen(false);
        }
        setPendingLibraryAnchor(null);
      });
    });
    return () => {
      window.cancelAnimationFrame(firstFrame);
      if (secondFrame) window.cancelAnimationFrame(secondFrame);
    };
  }, [pendingLibraryAnchor, reading.id, selectElement]);

  const selectNativeRange = useCallback(() => {
    const nativeSelection = window.getSelection();
    if (!nativeSelection || nativeSelection.isCollapsed || !nativeSelection.rangeCount) {
      return false;
    }
    const text = nativeSelection.toString().trim().replace(/\s+/g, " ");
    if (!text || text.length > 160) return false;
    const range = nativeSelection.getRangeAt(0);
    const commonNode = range.commonAncestorContainer;
    const commonElement =
      commonNode.nodeType === Node.ELEMENT_NODE
        ? (commonNode as Element)
        : commonNode.parentElement;
    if (!commonElement || !storyRef.current?.contains(commonElement)) return false;
    const paragraph = commonElement.closest<HTMLElement>("[data-reading-paragraph]");
    const key = normalizeWord(text);
    const rect = range.getBoundingClientRect();
    wordOrigin.current = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
    setWordMotion(panelMode === "word" ? "replace" : "open");
    setMeaningView(0);
    setSelection({
      word: text,
      key,
      tokenId: `range-${Date.now()}`,
      paragraphId: paragraph?.dataset.paragraphId || "p0",
      sentence: paragraph?.textContent?.trim() || text,
      curated: Boolean(words[key]),
    });
    setPanelMode("word");
    setDeepOpen(false);
    return true;
  }, [panelMode, words]);

  const handlePointerDown = (event: ReactPointerEvent<HTMLElement>) => {
    if (pendingTap.current !== null) {
      window.clearTimeout(pendingTap.current);
      pendingTap.current = null;
    }
    document
      .querySelectorAll(".tap-acknowledged")
      .forEach((element) => element.classList.remove("tap-acknowledged"));
    pointerStart.current = {
      x: event.clientX,
      y: event.clientY,
      at: window.performance.now(),
      pointerType: event.pointerType,
    };
  };

  const handlePointerUp = (event: ReactPointerEvent<HTMLElement>) => {
    const nativeSelection = window.getSelection();
    if (nativeSelection && !nativeSelection.isCollapsed) return;
    const distance = Math.hypot(
      event.clientX - pointerStart.current.x,
      event.clientY - pointerStart.current.y,
    );
    const duration = window.performance.now() - pointerStart.current.at;
    if (distance > 10 || (event.pointerType === "touch" && duration > 280)) return;
    const element = (event.target as HTMLElement).closest<HTMLElement>(
      "[data-selectable-word]",
    );
    if (!element) return;
    const tokenId = element.dataset.tokenId || "";
    const releasedAt = window.performance.now();
    if (
      event.pointerType === "touch"
      && lastTouchRelease.current.tokenId === tokenId
      && releasedAt - lastTouchRelease.current.at < 360
    ) {
      lastTouchRelease.current = { at: 0, tokenId: "" };
      element.classList.remove("tap-acknowledged");
      return;
    }
    if (event.pointerType === "touch") {
      lastTouchRelease.current = { at: releasedAt, tokenId };
    }
    element.classList.add("tap-acknowledged");
    const open = () => {
      element.classList.remove("tap-acknowledged");
      if (window.getSelection()?.isCollapsed === false) return;
      selectElement(element);
      pendingTap.current = null;
    };
    if (event.pointerType === "touch") {
      pendingTap.current = window.setTimeout(open, 300);
    } else {
      open();
    }
  };

  const handlePointerCancel = () => {
    if (pendingTap.current !== null) window.clearTimeout(pendingTap.current);
    pendingTap.current = null;
    document
      .querySelectorAll(".tap-acknowledged")
      .forEach((element) => element.classList.remove("tap-acknowledged"));
  };

  const handleStoryKeyDown = (event: ReactKeyboardEvent<HTMLElement>) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    const element = (event.target as HTMLElement).closest<HTMLElement>(
      ".enriched-word[data-selectable-word]",
    );
    if (!element) return;
    event.preventDefault();
    selectElement(element);
  };

  const closeWord = () => {
    if (selection) {
      setResumeCue({
        tokenId: selection.tokenId,
        paragraphId: selection.paragraphId,
        word: selection.word,
      });
      window.localStorage.setItem(
        `verbalore-last-anchor:${reading.id}`,
        JSON.stringify({
          tokenId: selection.tokenId,
          paragraphId: selection.paragraphId,
          word: selection.word,
        }),
      );
    }
    setPanelMode("empty");
    setSelection(null);
    setDeepOpen(false);
    window.getSelection()?.removeAllRanges();
  };

  const chooseAdjacent = (direction: -1 | 1) => {
    if (!selection || selection.tokenId.startsWith("range-")) return;
    const nextTokenId = direction < 0 ? adjacentTokenIds.previous : adjacentTokenIds.next;
    if (!nextTokenId) return;
    const next = document.querySelector<HTMLElement>(
      `[data-token-id="${CSS.escape(nextTokenId)}"]`,
    );
    if (next) selectElement(next);
  };

  const returnToSelection = () => {
    if (!selection) return;
    const target = selection.tokenId.startsWith("range-")
      ? document.querySelector<HTMLElement>(
          `[data-reading-paragraph][data-paragraph-id="${selection.paragraphId}"]`,
        )
      : document.querySelector<HTMLElement>(
          `[data-token-id="${selection.tokenId}"]`,
        );
    target?.scrollIntoView({ block: "center", behavior: "smooth" });
    window.setTimeout(() => target?.focus({ preventScroll: true }), 450);
  };

  const resumeReading = () => {
    const target = document.querySelector<HTMLElement>(
      `[data-reading-paragraph][data-paragraph-id="${resumeParagraph || "p0"}"]`,
    );
    target?.scrollIntoView({ block: "center", behavior: "smooth" });
  };

  const hearWord = () => {
    if (isPrivateReading) {
      notify("Pronunciation providers are withheld for private passages in this alpha.");
      return;
    }
    if (!selection || !("speechSynthesis" in window)) {
      notify("Pronunciation audio is unavailable on this device.");
      return;
    }
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(selection.word);
    const selectedVoice = speechVoices.find((voice) => voice.voiceURI === speechVoiceUri);
    if (selectedVoice) utterance.voice = selectedVoice;
    utterance.lang = selectedVoice?.lang || "en-GB";
    utterance.rate = 0.82;
    window.speechSynthesis.speak(utterance);
  };

  const chooseSpeechVoice = (voiceUri: string) => {
    setSpeechVoiceUri(voiceUri);
    window.localStorage.setItem("verbalore-pronunciation-voice", voiceUri);
  };

  const openLibrary = () => {
    if (!currentUser) {
      requestSignIn("Sign in to keep a vocabulary library across devices.");
      return;
    }
    if (window.location.hash !== "#read") {
      window.history.pushState({ verbaloreView: "read", verbalorePanel: "library" }, "", "#read");
    }
    setShowHome(false);
    setExperienceMode("read");
    setPanelMode("library");
  };

  const requestSignIn = useCallback((reason?: string) => {
    setAuthError("");
    setSignInOpen(true);
    if (reason) notify(reason);
  }, [notify]);

  const handleGoogleCredential = useCallback(async (credential: string) => {
    setSigningIn(true);
    setAuthError("");
    try {
      const response = await fetch("/api/auth/google", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ credential }),
      });
      const data = await response.json();
      if (!response.ok || !data.user) {
        throw new Error(data.error || "Google sign-in could not be completed.");
      }
      setCurrentUser(data.user);
      setProfile((current) => ({
        ...current,
        id: data.user.id,
        displayName: data.user.displayName,
      }));
      await loadLearnerData();
      setSignInOpen(false);
      notify("Welcome. Verbalore can now remember your learning.");
    } catch (error) {
      setAuthError(
        error instanceof Error
          ? error.message
          : "Google sign-in could not be completed.",
      );
    } finally {
      setSigningIn(false);
    }
  }, [loadLearnerData, notify]);

  const signOut = async () => {
    try {
      const response = await fetch("/api/auth/logout", { method: "POST" });
      if (!response.ok) throw new Error("Sign out failed");
      setCurrentUser(null);
      setProfile(DEFAULT_PROFILE);
      setSavedWords([]);
      setSavedContexts([]);
      setPrivateReadings([]);
      setPrivateReadingRecords({});
      setPracticeAttempts([]);
      setWordReviews([]);
      setReviewWord(null);
      setResumeParagraph(null);
      if (currentReadingId.startsWith("private:")) {
        setCurrentReadingId(READINGS[0].id);
        window.localStorage.setItem("verbalore-current-reading", READINGS[0].id);
      }
      progressReady.current = false;
      setPanelMode("empty");
      notify("Signed out. Reading remains open; private learning data stays in your account.");
    } catch {
      notify("Verbalore could not securely end this session yet. Please try again.");
    }
  };

  const saveWord = async () => {
    if (!selection || !insight) return;
    if (!currentUser) {
      requestSignIn("Sign in when you want Verbalore to remember a word.");
      return;
    }
    const sourceType = isPrivateReading ? "private_import" : "authored";
    const sourceId = isPrivateReading ? activePrivateId || "" : reading.id;
    const sourceSentence = isPrivateReading ? selection.sentence : insight.sentence;
    const existing = savedWords.find((word) => word.word === selection.key);
    const item: VocabularyItem = {
      word: selection.key,
      displayWord: selection.word,
      status: existing?.status || "Encountered",
      meaning: insight.meaning,
      sourceSentence,
      sourceAnchor: isPrivateReading
        ? `private:${sourceId}:${selection.paragraphId}`
        : `${sourceId}:${selection.paragraphId}`,
      lastOpenedSection: deepOpen ? "deep" : "glance",
    };
    setSavedWords((current) => [item, ...current.filter((word) => word.word !== item.word)]);
    const now = new Date().toISOString();
    const existingContext = savedContexts.find((context) =>
      context.word === item.word && context.sourceAnchor === item.sourceAnchor
    );
    const optimisticContext: VocabularyContext = {
      ...item,
      sourceType,
      sourceId,
      createdAt: existingContext?.createdAt || now,
      updatedAt: now,
    };
    setSavedContexts((current) => [
      optimisticContext,
      ...current.filter((context) => !(context.word === item.word && context.sourceAnchor === item.sourceAnchor)),
    ]);
    notify(existing ? "This encounter joined the word’s context." : "Saved with its sentence. Continue whenever you like.");
    try {
      const response = await fetch("/api/vocabulary", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...item,
          sourceType,
          sourceId,
          paragraphId: selection.paragraphId,
        }),
      });
      if (response.status === 401) {
        setCurrentUser(null);
        requestSignIn("Your session ended. Sign in again to save this word.");
        return;
      }
      if (!response.ok) throw new Error("Save failed");
      const data = await response.json();
      if (data.item) {
        setSavedWords((current) => [
          data.item,
          ...current.filter((word) => word.word !== data.item.word),
        ]);
        const reviewResponse = await fetch("/api/review");
        if (reviewResponse.ok) {
          const reviewData = await reviewResponse.json();
          if (reviewData.reviews) {
            setWordReviews(reviewData.reviews);
            setReviewWord((current) => current || data.item.word);
          }
        }
      }
    } catch {
      notify("Saved for this session; profile sync will retry later.");
    }
  };

  const removeWord = async (word: string) => {
    const removedItem = savedWords.find((item) => item.word === word);
    let mayReload = true;
    setSavedWords((current) => current.filter((item) => item.word !== word));
    setSavedContexts((current) => current.filter((context) => context.word !== word));
    setWordReviews((current) => current.filter((review) => review.item.word !== word));
    setReviewWord((current) => current === word ? null : current);
    try {
      const response = await fetch(`/api/vocabulary?word=${encodeURIComponent(word)}`, {
        method: "DELETE",
      });
      if (response.status === 401) {
        mayReload = false;
        setCurrentUser(null);
        requestSignIn("Your session ended. Sign in again before removing this word.");
        throw new Error("Session ended");
      }
      if (!response.ok) throw new Error("Remove failed");
      notify("Removed from your library and review history.");
    } catch {
      if (removedItem) {
        setSavedWords((current) => [
          removedItem,
          ...current.filter((item) => item.word !== word),
        ]);
      }
      notify("That word could not be removed; your library has been restored.");
      if (mayReload && currentUser) await loadLearnerData();
    }
  };

  const openSavedWord = async (item: VocabularyItem) => {
    if (item.sourceAnchor.startsWith("private:")) {
      const [, sourceId] = item.sourceAnchor.split(":");
      const opened = await openPrivateReading(sourceId);
      if (!opened) return;
      setPendingLibraryAnchor(item);
      return;
    }
    const separator = item.sourceAnchor.indexOf(":");
    const sourceReadingId = separator > 0 ? item.sourceAnchor.slice(0, separator) : reading.id;
    if (sourceReadingId !== reading.id && READING_BY_ID.has(sourceReadingId)) {
      const opened = await openReading(sourceReadingId);
      if (!opened) return;
    }
    setPendingLibraryAnchor(item);
  };

  const openImportDialog = () => {
    if (!currentUser) {
      requestSignIn("Sign in before bringing a private passage into Verbalore.");
      return;
    }
    setImportError("");
    setImportOpen(true);
  };

  const savePrivateReading = async () => {
    if (!importAccepted || importSource.trim().length < 80) return;
    setSavingImport(true);
    setImportError("");
    try {
      const response = await fetch("/api/imports", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: importTitle,
          sourceText: importSource,
          privacyAccepted: importAccepted,
        }),
      });
      const data = await response.json();
      if (response.status === 401) {
        setCurrentUser(null);
        setImportOpen(false);
        requestSignIn("Your session ended. Sign in again before saving this passage.");
        return;
      }
      if (!response.ok || !data.reading) {
        throw new Error(data.error || "Private passage could not be saved.");
      }
      const record = data.reading as PrivateReadingRecord;
      const { sourceText: _sourceText, ...summary } = record;
      void _sourceText;
      setPrivateReadings((current) => [
        summary,
        ...current.filter((item) => item.id !== record.id),
      ]);
      setImportTitle("");
      setImportSource("");
      setImportAccepted(false);
      activatePrivateReading(record);
      notify("Private passage saved. Nothing was analysed automatically.");
    } catch (error) {
      setImportError(error instanceof Error ? error.message : "Private passage could not be saved.");
    } finally {
      setSavingImport(false);
    }
  };

  const deletePrivateReading = async (id: string) => {
    try {
      const response = await fetch(`/api/imports?id=${encodeURIComponent(id)}`, {
        method: "DELETE",
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Private passage could not be deleted.");
      setPrivateReadings((current) => current.filter((item) => item.id !== id));
      setPrivateReadingRecords((current) => {
        const next = { ...current };
        delete next[id];
        return next;
      });
      setConfirmDeleteImport(null);
      if (currentReadingId === `private:${id}`) void openReading(READINGS[0].id);
      await loadLearnerData();
      setPanelMode("library");
      notify("Private passage and its derived private context were deleted.");
    } catch (error) {
      notify(error instanceof Error ? error.message : "Private passage could not be deleted.");
    }
  };

  const saveProfile = async () => {
    if (!currentUser) {
      requestSignIn("Sign in to create a private learning profile.");
      return;
    }
    try {
      const response = await fetch("/api/profile", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(profile),
      });
      if (response.status === 401) {
        setCurrentUser(null);
        requestSignIn("Your session ended. Sign in again to save these preferences.");
        return;
      }
      if (!response.ok) throw new Error("Profile save failed");
      const data = await response.json();
      if (data.profile) setProfile(data.profile);
      notify("Profile preferences saved.");
    } catch {
      notify("Preferences remain active; sync will retry later.");
    }
  };

  const toggleTheme = (event: ReactMouseEvent<HTMLButtonElement>) => {
    const source = event.currentTarget.getBoundingClientRect();
    document.documentElement.style.setProperty("--theme-origin-x", `${source.left + source.width / 2}px`);
    document.documentElement.style.setProperty("--theme-origin-y", `${source.top + source.height / 2}px`);
    const next = theme === "night" ? "paper" : "night";
    const transitionDuration = next === "paper" ? 1150 : 780;
    document.documentElement.style.setProperty("--theme-transition-duration", `${transitionDuration}ms`);
    document.documentElement.style.setProperty(
      "--theme-reveal-start-brightness",
      next === "paper" ? "0.72" : "1.08",
    );
    document.documentElement.style.setProperty(
      "--theme-reveal-mid-brightness",
      next === "paper" ? "0.9" : "1.03",
    );
    const applyTheme = () => {
      document.documentElement.dataset.theme = next;
      window.localStorage.setItem("verbalore-theme", next);
      setTheme(next);
    };
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      applyTheme();
      return;
    }

    const transitionDocument = document as Document & {
      startViewTransition?: (update: () => void) => { finished: Promise<void> };
    };
    if (transitionDocument.startViewTransition) {
      document.documentElement.classList.add("theme-view-transition");
      const transition = transitionDocument.startViewTransition(applyTheme);
      void transition.finished.finally(() => document.documentElement.classList.remove("theme-view-transition"));
      return;
    }

    document.documentElement.classList.add("theme-transition");
    window.requestAnimationFrame(applyTheme);
    window.setTimeout(() => document.documentElement.classList.remove("theme-transition"), transitionDuration + 70);
  };

  const chooseReadingSize = (next: ReadingSize) => {
    const anchor = document.querySelector<HTMLElement>(
      `[data-reading-paragraph][data-paragraph-id="${currentParagraph}"]`,
    );
    if (anchor) readingSizeAnchorRef.current = { element: anchor, top: anchor.getBoundingClientRect().top };
    setReadingSize(next);
    window.localStorage.setItem("verbalore-reading-size", next);
    if (readingSizeMenuRef.current) readingSizeMenuRef.current.open = false;
  };

  const switchExperience = (mode: ExperienceMode) => {
    setShowHome(false);
    setExperienceMode(mode);
    setPanelMode("empty");
    setSelection(null);
    setDeepOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const enterReadingRoom = () => {
    window.history.pushState({ verbaloreView: "read" }, "", "#read");
    setShowHome(false);
    setExperienceMode("read");
    setPanelMode("empty");
    window.scrollTo({ top: 0, behavior: "auto" });
  };

  const browseReadingsFromHome = () => {
    window.history.pushState({ verbaloreView: "read" }, "", "#read");
    setShowHome(false);
    setExperienceMode("read");
    setPanelMode("empty");
    setDiscoveryOpen(true);
    window.scrollTo({ top: 0, behavior: "auto" });
  };

  const enterPracticeRoom = (view: PracticeView = "words") => {
    window.history.pushState({ verbaloreView: "practice" }, "", "#practice");
    setShowHome(false);
    setPracticeView(view);
    setExperienceMode("practice");
    setPanelMode("empty");
    window.scrollTo({ top: 0, behavior: "auto" });
  };

  const changeExpressionStudy = (nextIndex: number) => {
    const nextStudy = ALL_EXPRESSION_STUDIES[nextIndex];
    if (!nextStudy || nextStudy.id === expressionStudy.id) return;
    setExpressionWork((current) => ({
      ...current,
      [expressionStudy.id]: {
        draft: practiceDraft,
        revision: practiceRevision,
        checked: practiceChecked,
        stanceId: activeStance.id,
      },
    }));
    const restored = expressionWork[nextStudy.id];
    setActiveExpressionStudyId(nextStudy.id);
    setPracticeDraft(restored?.draft || "");
    setPracticeRevision(restored?.revision || "");
    setPracticeChecked(restored?.checked || false);
    setActiveStanceId(restored?.stanceId || "tactful");
    window.localStorage.setItem("verbalore-expression-study", nextStudy.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const returnHome = () => {
    window.history.pushState({ verbaloreView: "home" }, "", window.location.pathname + window.location.search);
    setShowHome(true);
    setPanelMode("empty");
    setSelection(null);
    window.scrollTo({ top: 0, behavior: "auto" });
  };

  const openPracticeView = (view: PracticeView) => {
    setPracticeView(view);
    setReviewChoice("");
    setReviewDraft("");
    setReviewSignals([]);
    setReviewFeedback("");
    switchExperience("practice");
  };

  const chooseReviewWord = (word: string) => {
    setReviewWord(word);
    setReviewChoice("");
    setReviewDraft("");
    setReviewSignals([]);
    setReviewFeedback("");
  };

  const submitWordReview = async () => {
    if (!currentUser) {
      requestSignIn("Sign in to preserve evidence of the words you are learning.");
      return;
    }
    if (!currentReview || !currentReview.prompt) return;
    const step = currentReview.evidence.nextStep;
    if ((step === "recall" || step === "contrast") && !reviewChoice) {
      notify("Choose the meaning that feels most exact.");
      return;
    }
    if ((step === "use" || step === "natural") && !reviewDraft.trim()) {
      notify(step === "use" ? "Write one original use first." : "Record where the word entered real life.");
      return;
    }
    setSavingReview(true);
    setReviewFeedback("");
    try {
      const response = await fetch("/api/review", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          word: currentReview.item.word,
          promptType: step,
          answer: reviewChoice,
          response: reviewDraft,
          signals: reviewSignals,
        }),
      });
      if (response.status === 401) {
        setCurrentUser(null);
        requestSignIn("Your session ended. Sign in again to preserve this review.");
        return;
      }
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Review could not be saved.");
      setWordReviews((current) => current.map((review) =>
        review.item.word === currentReview.item.word
          ? { ...review, item: data.item, evidence: data.evidence }
          : review,
      ));
      setSavedWords((current) => current.map((item) =>
        item.word === data.item.word ? data.item : item,
      ));
      const outcome = data.attempt?.outcome;
      const statusChanged = data.item.status !== currentReview.item.status;
      setReviewFeedback(
        outcome === "not_yet"
          ? data.correction
            ? `Not yet. The authored answer is “${data.correction}”. Nothing has been lost; this is the evidence that decides what comes next.`
            : "Not yet. Revisit the criteria and keep the sentence on screen; an honest pause is more useful than a false success."
          : statusChanged
            ? `${currentReview.item.displayWord} is now ${data.item.status}. The change came from evidence, not a manual badge.`
            : step === "natural"
              ? "Natural use recorded. Verbalore keeps the context, not a streak."
              : "Evidence recorded. The next step has changed with it.",
      );
      setReviewChoice("");
      setReviewDraft("");
      setReviewSignals([]);
    } catch (error) {
      setReviewFeedback(error instanceof Error ? error.message : "Review could not be saved.");
    } finally {
      setSavingReview(false);
    }
  };

  const demoteCurrentWord = async () => {
    if (!currentReview || currentReview.evidence.status === "Encountered") return;
    const nextStatus = currentReview.evidence.status === "Owned" ? "Active" : "Encountered";
    try {
      const response = await fetch("/api/review", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ word: currentReview.item.word, status: nextStatus }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Status could not be changed.");
      setWordReviews((current) => current.map((review) =>
        review.item.word === currentReview.item.word
          ? { ...review, item: data.item, evidence: data.evidence }
          : review,
      ));
      setSavedWords((current) => current.map((item) =>
        item.word === data.item.word ? data.item : item,
      ));
      setReviewFeedback(`Moved back to ${nextStatus}. Earlier evidence remains visible in history, but new evidence must rebuild command.`);
    } catch (error) {
      setReviewFeedback(error instanceof Error ? error.message : "Status could not be changed.");
    }
  };

  const savePractice = async () => {
    if (!practiceDraft.trim()) {
      notify("Write a first attempt before saving it.");
      return;
    }
    if (!currentUser) {
      requestSignIn("Sign in only when you want this attempt preserved.");
      return;
    }
    setSavingPractice(true);
    try {
      const response = await fetch("/api/practice", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          promptId: expressionStudy.id,
          draft: practiceDraft,
          revision: practiceRevision,
          assistanceMode: profile.assistanceMode,
          signals: practiceAssessment.status === "assessed"
            ? [
                `stance:${activeStance.id}`,
                ...practiceSignals.filter((signal) => signal.met).map((signal) => signal.id),
              ]
            : [`stance:${activeStance.id}`],
        }),
      });
      if (response.status === 401) {
        setCurrentUser(null);
        requestSignIn("Your session ended. Sign in again to preserve this attempt.");
        return;
      }
      if (!response.ok) throw new Error("Practice save failed");
      const data = await response.json();
      if (data.attempt) {
        setPracticeAttempts((current) => [data.attempt, ...current]);
      }
      notify("Attempt saved. Your first wording and revision remain together.");
    } catch {
      notify("The attempt remains on screen, but could not be saved yet.");
    } finally {
      setSavingPractice(false);
    }
  };

  const reopenPractice = (attempt: PracticeAttempt) => {
    const attemptStudy = EXPRESSION_STUDY_BY_ID.get(attempt.promptId);
    if (attemptStudy) {
      setExpressionWork((current) => ({
        ...current,
        [expressionStudy.id]: {
          draft: practiceDraft,
          revision: practiceRevision,
          checked: practiceChecked,
          stanceId: activeStance.id,
        },
      }));
      setActiveExpressionStudyId(attemptStudy.id);
      window.localStorage.setItem("verbalore-expression-study", attemptStudy.id);
    }
    try {
      const signals = JSON.parse(attempt.signals) as string[];
      const stance = signals.find((signal) => signal.startsWith("stance:"))?.slice("stance:".length);
      if (stance && attemptStudy?.stances.some((candidate) => candidate.id === stance)) setActiveStanceId(stance);
    } catch {
      setActiveStanceId("tactful");
    }
    setPracticeDraft(attempt.draft);
    setPracticeRevision(attempt.revision || "");
    setPracticeChecked(true);
    document.getElementById("practice-editor")?.scrollIntoView({ block: "start", behavior: "smooth" });
  };

  const removePractice = async (id: number) => {
    setPracticeAttempts((current) => current.filter((attempt) => attempt.id !== id));
    await fetch(`/api/practice?id=${id}`, { method: "DELETE" }).catch(() => undefined);
  };

  if (showHome) {
    return <HomePage onBegin={enterReadingRoom} onBrowse={browseReadingsFromHome} onPractice={enterPracticeRoom} />;
  }

  return (
    <main className={`app-shell mode-${experienceMode}`} data-reading-size={readingSize}>
      <header className="topbar">
        <button className="brand brand-button" type="button" onClick={returnHome} aria-label="Verbalore home">
          <VerbaloreMark className="brand-mark" />
          <span>
            <strong>Verbalore</strong>
            <small>The Weight of Words</small>
          </span>
        </button>
        <nav className="experience-switch" aria-label="Learning world">
          <button type="button" className={experienceMode === "read" ? "active" : ""} onClick={() => switchExperience("read")}>
            <span>Read</span><small>Encounter language</small>
          </button>
          <button type="button" className={experienceMode === "practice" ? "active" : ""} onClick={() => openPracticeView("words")}>
            <span>Practise</span><small>Command language</small>
          </button>
        </nav>
        <div className="topbar-actions">
          <button className="mobile-library-button" type="button" onClick={openLibrary} aria-label="Open my learning library">
            <span aria-hidden="true">◇</span>
            {savedWords.length ? <small>{savedWords.length}</small> : null}
          </button>
          <button className="icon-button" type="button" onClick={toggleTheme} aria-label={theme === "night" ? "Switch to light theme" : "Switch to dark theme"}>
            <span className="theme-icon theme-icon-paper" aria-hidden="true">☾</span>
            <span className="theme-icon theme-icon-night" aria-hidden="true">☀</span>
          </button>
          <button
            className="profile-button"
            type="button"
            onClick={() => currentUser ? setPanelMode("profile") : requestSignIn()}
            aria-label={currentUser ? "Open learning profile" : "Sign in with Google"}
          >
            <span className="avatar">{currentUser ? profile.displayName.slice(0, 1).toUpperCase() : "V"}</span>
            <span>{!authReady || loadingProfile ? "Opening…" : currentUser ? profile.displayName : "Sign in"}</span>
          </button>
        </div>
      </header>

      {experienceMode === "read" ? (
        <div className="mobile-reading-progress" aria-label={`${progress}% read`}>
          <span style={{ width: `${progress}%` }} />
          <strong>{progress}%</strong>
        </div>
      ) : null}

      <div className="workspace">
        <aside className="lesson-rail" aria-label="Lesson navigation">
          {experienceMode === "read" ? (
            <button
              className="reading-summary-button"
              type="button"
              onClick={() => setDiscoveryOpen(true)}
              aria-label={`Browse stories. Current reading: ${reading.title}`}
            >
              <span><span className="lesson-label">Current reading</span><small>Browse ↗</small></span>
              <h2>{reading.title}</h2>
            </button>
          ) : (
            <>
              <div className="lesson-label">{practiceView === "words" ? "Words to command" : "Expression Studio"}</div>
              <h2>{practiceView === "words" ? "Encountered. Active. Owned." : expressionStudy.title}</h2>
            </>
          )}
          <p>{experienceMode === "read" ? reading.whyItFits : practiceView === "words" ? "Recognition, original use, later recall, and life beyond the exercise." : expressionStudy.skill}</p>
          {experienceMode === "read" ? <div className="progress-block" aria-label={`${progress}% read`}>
            <div className="progress-track"><span style={{ width: `${progress}%` }} /></div>
            <div><strong>{progress}%</strong><span>read</span></div>
          </div> : <div className="practice-count"><strong>{practiceView === "words" ? dueReviewCount : practiceAttempts.length}</strong><span>{practiceView === "words" ? "reviews ready" : "attempts preserved"}</span></div>}
          {experienceMode === "read" && resumeParagraph && progress > 6 ? (
            <button className="resume-button" type="button" onClick={resumeReading}>
              Resume where I left off
            </button>
          ) : null}
          <nav className="rail-nav">
            <button className={experienceMode === "read" ? "active" : ""} type="button" onClick={() => switchExperience("read")}>
              <span>01</span> Reading
            </button>
            <button className={experienceMode === "practice" && practiceView === "words" ? "active" : ""} type="button" onClick={() => openPracticeView("words")}>
              <span>02</span> Words to command
            </button>
            <button className={experienceMode === "practice" && practiceView === "expression" ? "active" : ""} type="button" onClick={() => openPracticeView("expression")}>
              <span>03</span> Expression Studio
            </button>
            <button type="button" onClick={openLibrary}>
              <span>◇</span> My library
            </button>
            <button type="button" onClick={() => currentUser ? setPanelMode("profile") : requestSignIn("Sign in to shape your learning profile.")}>
              <span>Aa</span> Learning profile
            </button>
          </nav>
          <div className="rail-note">
            <span aria-hidden="true">✦</span>
            <p>{experienceMode === "read" ? isPrivateReading ? <><strong>Private by design.</strong> This passage is not analysed or sent to an enrichment provider automatically.</> : <><strong>No detour is lost.</strong> Save a word now and return to its exact sentence later.</> : practiceView === "words" ? <><strong>No false mastery.</strong> A word changes state only when recall and use leave evidence.</> : <><strong>Your first wording matters.</strong> Compare it with the revision; do not erase the evidence of learning.</>}</p>
          </div>
        </aside>

        {experienceMode === "read" ? <section className="reading-stage" id="lesson-start">
          <div className="reading-tools">
            <span><i /> {isPrivateReading ? "Private passage" : "Enriched word"}</span>
            <span>{isPrivateReading ? "Nothing analysed automatically" : "Select any word or phrase"}</span>
            <details className="reading-size-settings" ref={readingSizeMenuRef}>
              <summary aria-label={`Reading size: ${readingSize}`} title="Reading size">Aa</summary>
              <div role="group" aria-label="Choose reading text size">
                {([
                  ["compact", "Compact", "A"],
                  ["balanced", "Balanced", "A"],
                  ["generous", "Generous", "A"],
                ] as const).map(([value, label, glyph]) => (
                  <button
                    key={value}
                    type="button"
                    className={readingSize === value ? "active" : ""}
                    aria-pressed={readingSize === value}
                    onClick={() => chooseReadingSize(value)}
                  >
                    <span aria-hidden="true">{glyph}</span><small>{label}</small>
                  </button>
                ))}
              </div>
            </details>
            <button type="button" onClick={selectNativeRange}>Explore current selection</button>
          </div>

          <article
            ref={storyRef}
            className="story"
            aria-labelledby="story-title"
            onPointerDown={handlePointerDown}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerCancel}
            onKeyDown={handleStoryKeyDown}
          >
            <header className="story-header">
              <span className="eyebrow">{reading.subtitle}</span>
              <h1 id="story-title">{reading.title}</h1>
              <p className="story-deck">{reading.deck}</p>
              <div className="ornament" aria-hidden="true"><span>✦</span></div>
            </header>

            <div className="story-body">
              {story.map((segment) => (
                <p
                  key={segment.id}
                  className={`story-paragraph ${segment.kind}`}
                  data-reading-paragraph
                  data-paragraph-id={segment.id}
                  data-sentence={segment.text}
                >
                  <StoryText segment={segment} words={words} />
                </p>
              ))}
            </div>

            <footer className="lesson-close">
              <span className="eyebrow">{reading.exercise.kicker}</span>
              <h2>{reading.exercise.title}</h2>
              <p>{reading.exercise.prompt}{reading.exercise.targetWords.length ? <> Use {reading.exercise.targetWords.map((word, index) => <span key={word}>{index ? ", " : ""}<em>{word}</em></span>)}.</> : null}</p>
              <div className="lesson-close-actions">
                {isPrivateReading ? (
                  <button type="button" onClick={openLibrary}>Return to my library <span>→</span></button>
                ) : (
                  <button type="button" onClick={() => openPracticeView("expression")}>Begin when ready <span>→</span></button>
                )}
                <button className="next-reading-action" type="button" onClick={() => setDiscoveryOpen(true)}>Choose another reading <span>↗</span></button>
              </div>
            </footer>
          </article>
        </section> : practiceView === "words" ? (
          <section className="word-command-stage" id="word-command-start" aria-labelledby="word-command-title">
            <div className="practice-toolbar">
              <span><i /> Authored word practice</span>
              <span>{dueReviewCount} ready now</span>
              <strong>No streak · no AI judgment</strong>
            </div>

            <article className="word-command-workspace">
              <header className="command-header">
                <span className="eyebrow">Words to Command</span>
                <h1 id="word-command-title">Recognition is a beginning.<br /><em>Use is the proof.</em></h1>
                <p>A saved word is Encountered. Recall and an original sentence make it Active. Later recall and natural use may make it Owned.</p>
              </header>

              {!currentUser ? (
                <section className="command-empty">
                  <span aria-hidden="true">◇</span>
                  <h2>Read before you register.</h2>
                  <p>Every lesson remains open. Sign in only when you want Verbalore to preserve a word and the evidence of learning it.</p>
                  <button className="primary-action" type="button" onClick={() => requestSignIn()}>Sign in to keep my learning</button>
                </section>
              ) : !wordReviews.length ? (
                <section className="command-empty">
                  <span aria-hidden="true">Aa</span>
                  <h2>Your first word begins in the story.</h2>
                  <p>Save any enriched word. It will arrive here with its original sentence and one quiet next step.</p>
                  <button className="primary-action" type="button" onClick={() => switchExperience("read")}>Return to the reading</button>
                </section>
              ) : !currentReview || !currentReview.prompt ? (
                <section className="command-empty">
                  <span aria-hidden="true">◇</span>
                  <h2>Context is preserved; practice is being authored.</h2>
                  <p>Your saved selections remain safe. Curated command practice is available for the enriched words in this lesson.</p>
                  <button className="primary-action" type="button" onClick={() => switchExperience("read")}>Choose an enriched word</button>
                </section>
              ) : (
                <>
                  <nav className="word-queue" aria-label="Saved words ready for practice">
                    {actionableReviews.map((review) => (
                      <button
                        key={review.item.word}
                        type="button"
                        className={review.item.word === currentReview.item.word ? "active" : ""}
                        onClick={() => chooseReviewWord(review.item.word)}
                        aria-current={review.item.word === currentReview.item.word ? "true" : undefined}
                      >
                        <strong>{review.item.displayWord}</strong>
                        <span>{review.evidence.status}{review.evidence.due ? " · ready" : ""}</span>
                      </button>
                    ))}
                  </nav>

                  <section className="command-card" aria-labelledby="current-command-word">
                    <div className="command-card-heading">
                      <div>
                        <span className="section-kicker">{reviewStepName(currentReview.evidence.nextStep)}</span>
                        <h2 id="current-command-word">{currentReview.item.displayWord}</h2>
                      </div>
                      <div className={`status-seal status-${currentReview.evidence.status.toLowerCase()}`}>
                        <span>Current state</span>
                        <strong>{currentReview.evidence.status}</strong>
                      </div>
                    </div>

                    <blockquote className="command-context">“{currentReview.item.sourceSentence}”</blockquote>
                    {currentReview.item.sourceAnchor.startsWith("private:") ? (
                      <p className="private-review-boundary">Private-source boundary: this practice uses Verbalore’s maintained sense of the word. It does not verify that the private passage intended the same sense.</p>
                    ) : null}

                    <div className="command-ladder" aria-label={`Learning state: ${currentReview.evidence.status}`}>
                      {["Encountered", "Active", "Owned"].map((status, index) => (
                        <div
                          key={status}
                          className={index < vocabularyStatusRank(currentReview.evidence.status) ? "complete" : index === vocabularyStatusRank(currentReview.evidence.status) ? "current" : "future"}
                        >
                          <span aria-hidden="true">{index < vocabularyStatusRank(currentReview.evidence.status) ? "✓" : index + 1}</span>
                          <div><strong>{status}</strong><small>{status === "Encountered" ? "saved in context" : status === "Active" ? "recalled and used" : "recalled later, used naturally"}</small></div>
                        </div>
                      ))}
                    </div>

                    <div className="evidence-strip">
                      <div><strong>{currentReview.evidence.successfulChecks}</strong><span>accurate checks</span></div>
                      <div><strong>{currentReview.evidence.useAttempts}</strong><span>original uses</span></div>
                      <div><strong>{currentReview.evidence.naturalUses}</strong><span>natural uses</span></div>
                      <div><strong>{currentReview.evidence.due ? "Now" : calmReviewDate(currentReview.evidence.nextReviewAt)}</strong><span>next return</span></div>
                    </div>

                    <section className="review-prompt" aria-live="polite">
                      {currentReview.evidence.nextStep === "recall" ? (
                        <>
                          <span className="section-kicker">Restore the sentence</span>
                          <h3>{currentReview.prompt.recall.stem}</h3>
                          <div className="choice-list">
                            {currentReview.prompt.recall.options.map((option) => (
                              <button key={option} type="button" className={reviewChoice === option ? "selected" : ""} onClick={() => setReviewChoice(option)} aria-pressed={reviewChoice === option}>{option}</button>
                            ))}
                          </div>
                        </>
                      ) : currentReview.evidence.nextStep === "contrast" ? (
                        <>
                          <span className="section-kicker">Choose the exact shade</span>
                          <h3>{currentReview.prompt.contrast.question}</h3>
                          <div className="choice-list prose-choices">
                            {currentReview.prompt.contrast.options.map((option) => (
                              <button key={option} type="button" className={reviewChoice === option ? "selected" : ""} onClick={() => setReviewChoice(option)} aria-pressed={reviewChoice === option}>{option}</button>
                            ))}
                          </div>
                        </>
                      ) : currentReview.evidence.nextStep === "use" ? (
                        <>
                          <span className="section-kicker">An original use</span>
                          <h3>{currentReview.prompt.use.direction}</h3>
                          <label className="writing-field command-writing">
                            <span>Your sentence</span>
                            <textarea value={reviewDraft} onChange={(event) => setReviewDraft(event.target.value)} rows={4} placeholder={`Write with ${currentReview.item.displayWord}…`} />
                          </label>
                          <fieldset className="self-checks">
                            <legend>Check only what is genuinely true</legend>
                            {currentReview.prompt.use.criteria.map((criterion) => (
                              <label key={criterion.id}>
                                <input
                                  type="checkbox"
                                  checked={reviewSignals.includes(criterion.id)}
                                  onChange={(event) => setReviewSignals((current) => event.target.checked ? [...current, criterion.id] : current.filter((signal) => signal !== criterion.id))}
                                />
                                <span>{criterion.label}</span>
                              </label>
                            ))}
                          </fieldset>
                        </>
                      ) : (
                        <>
                          <span className="section-kicker">Life beyond the exercise</span>
                          <h3>{currentReview.prompt.naturalCue}</h3>
                          <label className="writing-field command-writing">
                            <span>A brief context note</span>
                            <textarea value={reviewDraft} onChange={(event) => setReviewDraft(event.target.value)} rows={4} placeholder="I used it when… / I noticed it in…" />
                          </label>
                          <p className="natural-note">This is learner-reported evidence. Verbalore records it honestly; it does not pretend to have heard the conversation.</p>
                        </>
                      )}

                      <div className="review-actions">
                        <button className="primary-action" type="button" onClick={submitWordReview} disabled={savingReview}>
                          {savingReview ? "Preserving…" : currentReview.evidence.nextStep === "natural" ? "Record this use" : "Check and continue"}
                        </button>
                        {currentReview.evidence.status !== "Encountered" ? (
                          <button className="secondary-action" type="button" onClick={demoteCurrentWord}>This word feels less secure</button>
                        ) : null}
                      </div>
                      {reviewFeedback ? <p className="review-feedback" role="status">{reviewFeedback}</p> : null}
                    </section>
                  </section>

                  <footer className="command-principle">
                    <strong>Ownership is deliberately difficult.</strong>
                    <p>Two accurate checks separated by at least a day, one original use, and one natural use are required. You may move a word backward whenever the badge feels more confident than you do.</p>
                  </footer>
                </>
              )}
            </article>
          </section>
        ) : (
          <section className="practice-stage" id="practice-start" aria-labelledby="practice-title">
            <div className="practice-toolbar">
              <span><i /> Authored guidance</span>
              <span>{profile.assistanceMode}</span>
              <strong>No AI · pattern check · nothing stored until you save</strong>
            </div>

            <article className="practice-workspace">
              <header className="practice-header">
                <span className="eyebrow">Expression Studio · Study {expressionStudy.number} · {expressionStudy.size} · {expressionStudy.minutes} min</span>
                <h1 id="practice-title">
                  {expressionTitleParts[0]}{expressionTitleParts.length > 1 ? <>,<br /><em>{expressionTitleParts.slice(1).join(", ")}.</em></> : "."}
                </h1>
                <p>{expressionStudy.concept}</p>
                <nav className="expression-study-nav" aria-label="Expression studies">
                  <button type="button" onClick={() => changeExpressionStudy(expressionStudyIndex - 1)} disabled={expressionStudyIndex === 0}>← Previous</button>
                  <strong>{expressionStudyIndex + 1} of {ALL_EXPRESSION_STUDIES.length}</strong>
                  <button type="button" onClick={() => changeExpressionStudy(expressionStudyIndex + 1)} disabled={expressionStudyIndex === ALL_EXPRESSION_STUDIES.length - 1}>Next →</button>
                </nav>
              </header>

              <section className="practice-brief" aria-labelledby="situation-title">
                <div className="step-number">01</div>
                <div>
                  <span className="section-kicker" id="situation-title">The situation</span>
                  <p>{expressionStudy.situation}</p>
                  <blockquote>“{expressionStudy.tangledDraft}”</blockquote>
                  <div className="diagnosis-grid">
                    <div><span>Intention</span><strong>{expressionStudy.skill}</strong></div>
                    <div><span>Invariant</span><strong>{expressionStudy.invariant}</strong></div>
                    <div><span>Craft</span><strong>{expressionStudy.craft.join(" · ")}</strong></div>
                  </div>
                </div>
              </section>

              <section className="verbal-range-study" aria-labelledby="verbal-range-title">
                <div className="step-number">02</div>
                <div>
                  <span className="section-kicker">Verbal Range</span>
                  <h2 id="verbal-range-title">Hold the meaning still. Change the landing.</h2>
                  <div className="range-contract">
                    <span>Meaning held constant</span>
                    <p>{expressionStudy.intention}</p>
                    <small>{expressionStudy.invariant}</small>
                  </div>

                  <div className="stance-tabs" aria-label="Choose an articulation stance">
                    {expressionStudy.stances.map((stance) => (
                      <button
                        key={stance.id}
                        type="button"
                        className={stance.id === activeStance.id ? "active" : ""}
                        aria-pressed={stance.id === activeStance.id}
                        onClick={() => setActiveStanceId(stance.id)}
                      >
                        {stance.label}
                      </button>
                    ))}
                  </div>

                  <article className="stance-card" key={activeStance.id} aria-live="polite">
                    <div className="stance-reading">
                      <span>{activeStance.label} · {activeStance.landing}</span>
                      <blockquote>“{activeStance.line}”</blockquote>
                    </div>
                    <div className="range-notes">
                      <div><span>What it protects</span><p>{activeStance.protects}</p></div>
                      <div><span>What it risks</span><p>{activeStance.tradeoff}</p></div>
                    </div>
                  </article>

                  <p className="range-principle">No version is ranked best. The situation decides which resource earns its place.</p>
                </div>
              </section>

              <section className="practice-editor" id="practice-editor" aria-labelledby="first-attempt-title">
                <div className="step-number">03</div>
                <div>
                  <span className="section-kicker" id="first-attempt-title">Your first attempt</span>
                  <h2>Put the intention in your own words.</h2>
                  <p className="editor-direction">{expressionStudy.practiceCue} This exercise checks visible structure, not whether a machine agrees with your meaning.</p>
                  <label className="writing-field">
                    <span>What would you say?</span>
                    <textarea
                      value={practiceDraft}
                      onChange={(event) => setPracticeDraft(event.target.value)}
                      placeholder="Write the line you would actually say…"
                      rows={5}
                    />
                    <small>{practiceDraft.trim() ? practiceDraft.trim().split(/\s+/).length : 0} words · private until saved</small>
                  </label>
                  <button
                    className="check-expression"
                    type="button"
                    onClick={() => setPracticeChecked(true)}
                    disabled={!practiceDraft.trim() && profile.assistanceMode !== "Show me"}
                  >
                    {profile.assistanceMode === "Show me" ? "Show me how the sentence changes" : "Check its visible structure"}
                    <span>→</span>
                  </button>
                </div>
              </section>

              {practiceChecked ? (
                <div className="practice-reveal">
                  <section className="signal-section" aria-labelledby="signal-title">
                    <div className="step-number">04</div>
                    <div>
                      <span className="section-kicker" id="signal-title">Authored structure check · not AI</span>
                      <h2>{practiceAssessment.status === "assessed" ? "Four visible features of this articulation." : "This response was not assessed."}</h2>
                      <p className="pattern-check-note">{practiceAssessment.message}</p>
                      {practiceAssessment.status === "assessed" ? (
                        <div className="signal-list">
                          {practiceSignals.map((signal) => (
                            <div className={signal.met ? "met" : "open"} key={signal.id}>
                              <span aria-hidden="true">{signal.met ? "✓" : "○"}</span>
                              <div><strong>{signal.label}</strong><p>{signal.guidance}</p></div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="assessment-boundary" role="status">
                          <strong>Not assessed</strong>
                          <p>No ticks or scores are shown when the response is outside this exercise’s narrow pattern.</p>
                        </div>
                      )}
                    </div>
                  </section>

                  <section className="revision-section" aria-labelledby="revision-title">
                    <div className="step-number">05</div>
                    <div>
                      <span className="section-kicker" id="revision-title">Revision</span>
                      <h2>Keep your meaning. Change its posture.</h2>
                      <label className="writing-field revision-field">
                        <span>Your deliberate version</span>
                        <textarea
                          value={practiceRevision}
                          onChange={(event) => setPracticeRevision(event.target.value)}
                          placeholder={`Revise in a ${activeStance.label.toLocaleLowerCase()} stance, or choose another landing above.`}
                          rows={5}
                        />
                        <small>{practiceRevision.trim() ? practiceRevision.trim().split(/\s+/).length : 0} words</small>
                      </label>
                      <div className="practice-actions">
                        <button className="primary-action" type="button" onClick={savePractice} disabled={savingPractice}>
                          {savingPractice ? "Saving…" : "Save this attempt"}
                        </button>
                        <button className="secondary-action" type="button" onClick={() => { setPracticeDraft(""); setPracticeRevision(""); setPracticeChecked(false); }}>
                          Begin again
                        </button>
                      </div>
              <p className="storage-note">Saving preserves both versions in your private learning profile so you can see the change instead of remembering only the polished result.</p>
                    </div>
                  </section>
                </div>
              ) : null}

              <section className="attempt-history" aria-labelledby="history-title">
                <div className="history-heading">
                  <div><span className="section-kicker">Return without losing anything</span><h2 id="history-title">Earlier attempts</h2></div>
                  <strong>{String(practiceAttempts.length).padStart(2, "0")}</strong>
                </div>
                {practiceAttempts.length ? (
                  <ul>
                    {practiceAttempts.map((attempt) => (
                      <li key={attempt.id}>
                        <button type="button" onClick={() => reopenPractice(attempt)}>
                          <span>{EXPRESSION_STUDY_BY_ID.get(attempt.promptId)?.title || "Expression study"} · {attempt.status}</span>
                          <blockquote>“{attempt.revision || attempt.draft}”</blockquote>
                          <small>{new Date(attempt.updatedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short" })} · reopen</small>
                        </button>
                        <button className="delete-attempt" type="button" onClick={() => removePractice(attempt.id)} aria-label="Delete this practice attempt">×</button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="empty-attempts">Your saved attempts will appear here. No streak, no pressure. Only evidence that your language is changing.</p>
                )}
              </section>
            </article>
          </section>
        )}

        {experienceMode === "read" && panelMode === "word" ? (
          <button
            className="word-sheet-scrim"
            type="button"
            onClick={closeWord}
            aria-label="Close word explanation and return to the reading"
          />
        ) : null}

        <aside ref={inspectorRef} className={`inspector ${panelMode === "word" && deepOpen ? "deep" : ""}`}>
          <p className="sr-only" role="status" aria-live="polite">
            {panelMode === "word" && selection && insight
              ? `Opened ${selection.word}. ${meaningLabel}: ${insight.meaning}`
              : ""}
          </p>
          {experienceMode === "practice" && panelMode === "empty" ? (
            <div className="practice-guide">
              <span className="inspector-number">02</span>
              {practiceView === "words" ? (
                <>
                  <span className="section-kicker">The evidence</span>
                  <h2>Command, not collection.</h2>
                  <p>A large library can disguise a small usable vocabulary. Verbalore advances one word only when its evidence changes.</p>
                  <ol>
                    <li><span>1</span><div><strong>Context</strong><p>Keep the sentence in which the word first mattered.</p></div></li>
                    <li><span>2</span><div><strong>Recall</strong><p>Restore the word and distinguish its exact shade.</p></div></li>
                    <li><span>3</span><div><strong>Use</strong><p>Write an original sentence against authored criteria.</p></div></li>
                    <li><span>4</span><div><strong>Return</strong><p>Recall it later, then notice it entering real language.</p></div></li>
                  </ol>
                  <blockquote>“Encountered is honest.<br />Owned must be earned.”</blockquote>
                  <p className="guide-footnote">No streak and no automatic AI judgment. The record shows what happened; it never pretends to know more than it observed.</p>
                </>
              ) : (
                <>
                  <span className="section-kicker">The method</span>
                  <h2>Conceptual distillation</h2>
                  <p>First name the thought beneath the tangle. Then rebuild the sentence around that name.</p>
                  <ol>
                    <li><span>1</span><div><strong>Intention</strong><p>What outcome do you actually want?</p></div></li>
                    <li><span>2</span><div><strong>Tension</strong><p>Which two values must the sentence hold together?</p></div></li>
                    <li><span>3</span><div><strong>Concept</strong><p>Give the central distinction a short, memorable name.</p></div></li>
                    <li><span>4</span><div><strong>Compression</strong><p>Remove every phrase that does not carry meaning, tone, or rhythm.</p></div></li>
                  </ol>
                  <blockquote>“A boundary,<br />not a favour.”</blockquote>
                  <p className="guide-footnote">This is authored instruction. An eventual AI coach may apply the method to personal situations, but the method must remain useful without it.</p>
                </>
              )}
            </div>
          ) : null}

          {experienceMode === "read" && panelMode === "empty" ? (
            <div className="empty-inspector">
              <span className="inspector-number">01</span>
              <div className="empty-glyph" aria-hidden="true">Aa</div>
              <h2>A word may be a doorway.</h2>
              <p>Tap any word for a maintained local meaning. Underlined words add Verbalore’s contextual teaching layer.</p>
              <ol>
                <li><span>1</span> Glance for enough meaning to continue.</li>
                <li><span>2</span> Explore when curiosity takes hold.</li>
                <li><span>3</span> Return to the exact sentence.</li>
              </ol>
            </div>
          ) : null}

          {experienceMode === "read" && panelMode === "word" && selection && insight ? (
            <div className="word-panel" key={selection.tokenId} data-selection-id={selection.tokenId} data-motion={wordMotion}>
              <div className="panel-header">
                <span>{deepOpen ? (dictionaryOnly ? "Dictionary view" : "Deep view") : "Glance"}</span>
                <button type="button" onClick={closeWord} aria-label="Close word explanation">×</button>
              </div>

              <blockquote className="context-anchor">
                <span>From the sentence</span>
                “{contextSentence}”
              </blockquote>

              {isPrivateReading ? (
                <div className="private-sense-boundary" role="note">
                  <strong>{dictionaryEntry || privateBundledInsight ? "Sense not verified for this private sentence" : "No local entry for this selection"}</strong>
                  <p>{dictionaryEntry
                    ? "This word was resolved against Verbalore’s on-device lesson lexicon. Your passage was not analysed or sent anywhere, and this may not be the sense intended here."
                    : privateBundledInsight
                      ? "This lexical material comes from a maintained Verbalore reading. Your passage was not analysed, and this sense may not be the one intended here."
                      : "Verbalore made no external lookup and sent no passage text to an AI, dictionary, translation, or pronunciation provider."}</p>
                </div>
              ) : null}

              <div className="selection-controls" aria-label="Adjust selected word">
                <button type="button" onClick={() => chooseAdjacent(-1)} disabled={!adjacentTokenIds.previous} aria-label="Select previous word">←</button>
                <span>Adjust the selection</span>
                <button type="button" onClick={() => chooseAdjacent(1)} disabled={!adjacentTokenIds.next} aria-label="Select next word">→</button>
              </div>

              <div className="word-heading">
                <div>
                  <h2>{selection.word}</h2>
                  <p>{insight.partOfSpeech} · {insight.phonetic}</p>
                </div>
                {!isPrivateReading ? (
                  <button className="speak-button" type="button" onClick={hearWord} aria-label={`Hear ${selection.word} pronounced`}>
                    <span aria-hidden="true">▶</span> Hear
                  </button>
                ) : null}
              </div>

              <section className={`meaning-block ${activeMeaning?.authored ? "authored-meaning" : "lexical-meaning"}`} aria-labelledby="meaning-title">
                <h3 id="meaning-title">{activeMeaning?.label || meaningLabel}</h3>
                <p>{activeMeaning?.text || insight.meaning}</p>
                {meaningViews.length > 1 ? (
                  <button
                    className="alternate-meaning"
                    type="button"
                    onClick={() => setMeaningView((current) => (current + 1) % meaningViews.length)}
                  >
                    Still not landing? Try another explanation or sense
                    <small>{meaningView + 1} of {meaningViews.length}</small>
                  </button>
                ) : null}
              </section>

              {!noLexicalEntry ? <div className="quick-pair">
                {dictionaryOnly && dictionaryEntry ? (
                  <>
                    <div><span>Base word</span><strong>{dictionaryEntry.lemma}</strong></div>
                    <div><span>Nearest listed word</span><strong>{insight.synonym}</strong></div>
                  </>
                ) : (
                  <>
                    <div><span>In {profile.preferredLanguage}</span><strong>{insight.translations[profile.preferredLanguage]}</strong></div>
                    <div><span>Nearest useful word</span><strong>{insight.synonym}</strong></div>
                  </>
                )}
              </div> : null}

              <div className="panel-actions">
                <button className="secondary-action" type="button" onClick={saveWord}>
                  {isSaved ? "Add this context" : "Save for later"}
                </button>
                {!noLexicalEntry ? <button className="primary-action" type="button" onClick={() => setDeepOpen((open) => !open)}>
                  {deepOpen ? "Return to glance" : dictionaryOnly ? "Explore dictionary" : "Explore the word"}
                </button> : null}
              </div>

              {deepOpen ? (
                dictionaryOnly && dictionaryEntry ? (
                  <div className="deep-content dictionary-depth">
                    <section>
                      <h3 className="section-kicker">Available senses</h3>
                      <ol className="dictionary-senses">
                        {dictionaryEntry.senses.map((sense, index) => (
                          <li key={sense.id}>
                            <span>{index + 1} · {sense.partOfSpeech}</span>
                            <p>{displayLexicalText(sense.definition)}</p>
                            {sense.examples[0] ? <blockquote>“{displayLexicalText(sense.examples[0])}”</blockquote> : null}
                          </li>
                        ))}
                      </ol>
                    </section>
                    <section>
                      <h3 className="section-kicker">What this entry can tell you</h3>
                      <p>This maintained entry defines the word without pretending to know which sense this sentence intends. Words taught in context by Verbalore carry that additional layer.</p>
                    </section>
                    <section className="word-family">
                      <h3 className="section-kicker">Word family</h3>
                      <p>{insight.family}</p>
                    </section>
                    <section className="lexicon-source">
                      <h3 className="section-kicker">Source</h3>
                      <p>{dictionaryEntry.source}{dictionaryEntry.source.includes("Open English WordNet") ? " · CC BY 4.0" : ""}</p>
                    </section>
                  </div>
                ) : (
                <div className="deep-content">
                  <section className="authored-note">
                    <h3 className="section-kicker">Verbalore note · interpretation</h3>
                    <p>{insight.shade}</p>
                    <div className="opposites"><span>Nearby word</span><strong>{insight.synonym}</strong><i>↔</i><span>Useful contrast</span><strong>{insight.antonym}</strong></div>
                  </section>
                  {dictionaryEntry?.senseStatus === "dictionary" ? (
                    <section className="lexical-record">
                      <h3 className="section-kicker">Dictionary record · factual</h3>
                      <ol className="dictionary-senses">
                        {dictionaryEntry.senses.map((sense, index) => (
                          <li key={sense.id}>
                            <span>{index + 1} · {sense.partOfSpeech}</span>
                            <p>{displayLexicalText(sense.definition)}</p>
                            {sense.synonyms.length ? <small>Nearby: {sense.synonyms.slice(0, 5).join(" · ")}</small> : null}
                            {sense.examples[0] ? <blockquote>“{displayLexicalText(sense.examples[0])}”</blockquote> : null}
                          </li>
                        ))}
                      </ol>
                      <p className="lexicon-source">{dictionaryEntry.source}{dictionaryEntry.source.includes("Open English WordNet") ? " · CC BY 4.0" : ""}</p>
                    </section>
                  ) : null}
                  {isPrivateReading ? (
                    <section>
                      <h3 className="section-kicker">What remains private</h3>
                      <p>These details remain unavailable for private passages until you explicitly choose a trusted service.</p>
                    </section>
                  ) : (
                    <>
                      <section>
                        <h3 className="section-kicker">Why this word here?</h3>
                        <p>{insight.whyHere}</p>
                      </section>
                      <section>
                        <h3 className="section-kicker">Voice and intonation</h3>
                        <p>{insight.intonation}</p>
                        <button className="text-action" type="button" onClick={hearWord}>Hear it once more →</button>
                        <details className="voice-settings">
                          <summary>Accent and device voice</summary>
                          <label>
                            <span>Pronunciation voice</span>
                            <select value={speechVoiceUri} onChange={(event) => chooseSpeechVoice(event.target.value)}>
                              <option value="default">Use device default · British English fallback</option>
                              {speechVoices.map((voice) => (
                                <option key={voice.voiceURI} value={voice.voiceURI}>{pronunciationLabel(voice)}</option>
                              ))}
                            </select>
                          </label>
                          <p>These voices are supplied by this device or browser. Availability can differ on another device.</p>
                        </details>
                      </section>
                    </>
                  )}
                  <section className="word-family">
                    <h3 className="section-kicker">Word family</h3>
                    <p>{insight.family}</p>
                  </section>
                  <section className="example-card">
                    <h3 className="section-kicker">See it at work</h3>
                    <blockquote>“{insight.example}”</blockquote>
                  </section>
                </div>
                )
              ) : null}

              <button className="continue-button" type="button" onClick={closeWord}>
                Return to the marked sentence <span>↓</span>
              </button>
            </div>
          ) : null}

          {experienceMode === "read" && panelMode === "library" ? (
            <div className="library-panel">
              <div className="panel-header"><span>My library</span><button type="button" onClick={() => setPanelMode("empty")} aria-label="Close library">×</button></div>
              <div className="library-intro">
                <span className="section-kicker">Return with the context intact</span>
                <h2>Your learning has a history.</h2>
                <p>Continue a private passage or return to the sentence that first gave a word weight.</p>
                <button className="library-continue" type="button" onClick={() => setPanelMode("empty")}>
                  <span><small>Continue reading</small><strong>{reading.title}</strong></span>
                  <span>{progress}% <i aria-hidden="true">→</i></span>
                </button>
                <button className="bring-passage-action" type="button" onClick={openImportDialog}>
                  <span aria-hidden="true">＋</span><span><strong>Bring a passage</strong><small>Paste plain text · private to this account</small></span>
                </button>
              </div>

              {privateReadings.length ? (
                <section className="library-section private-reading-list" aria-labelledby="private-readings-title">
                  <div className="library-section-heading">
                    <span className="section-kicker" id="private-readings-title">Private readings</span>
                    <small>Stored only because you chose to save them</small>
                  </div>
                  <div className="private-reading-cards">
                    {privateReadings.map((item) => (
                      <article key={item.id} className={currentReadingId === `private:${item.id}` ? "current" : ""}>
                        <button className="private-reading-open" type="button" onClick={() => void openPrivateReading(item.id)}>
                          <span>{currentReadingId === `private:${item.id}` ? "Open now" : `${item.wordCount} words`}</span>
                          <strong>{item.title}</strong>
                          <p>{item.preview}</p>
                          <small>{new Date(item.updatedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short" })} · continue →</small>
                        </button>
                        {confirmDeleteImport === item.id ? (
                          <div className="private-delete-confirm" role="group" aria-label={`Delete ${item.title} permanently`}>
                            <p>Delete the source and its private saved context?</p>
                            <button type="button" onClick={() => void deletePrivateReading(item.id)}>Delete permanently</button>
                            <button type="button" onClick={() => setConfirmDeleteImport(null)}>Cancel</button>
                          </div>
                        ) : (
                          <button className="private-reading-delete" type="button" onClick={() => setConfirmDeleteImport(item.id)} aria-label={`Delete private reading ${item.title}`}>Delete…</button>
                        )}
                      </article>
                    ))}
                  </div>
                </section>
              ) : null}

              <section className="library-section" aria-labelledby="saved-words-title">
                <div className="library-section-heading">
                  <span className="section-kicker" id="saved-words-title">Words in context</span>
                  <small>{savedWords.length ? `${savedWords.length} learning ${savedWords.length === 1 ? "thread" : "threads"}` : "No thread begun yet"}</small>
                </div>
                {savedWords.length ? (
                  <div className="learning-threads">
                    {savedWords.map((item) => {
                      const isPrivateContext = item.sourceAnchor.startsWith("private:");
                      const sourceParts = item.sourceAnchor.split(":");
                      const fallbackContext: VocabularyContext = {
                        word: item.word,
                        displayWord: item.displayWord,
                        meaning: item.meaning,
                        sourceSentence: item.sourceSentence,
                        sourceAnchor: item.sourceAnchor,
                        sourceType: isPrivateContext ? "private_import" : "authored",
                        sourceId: isPrivateContext ? sourceParts[1] || "" : sourceParts[0] || "",
                        createdAt: item.createdAt,
                        updatedAt: item.updatedAt,
                      };
                      const contexts = contextsByWord.get(item.word) || [fallbackContext];
                      const review = reviewsByWord.get(item.word);
                      return (
                        <article className="learning-thread" key={item.word}>
                          <button className="learning-thread-return" type="button" onClick={() => void openSavedWord(item)}>
                            <span className="learning-thread-title">
                              <strong>{item.displayWord}</strong>
                              <small>{item.status}</small>
                            </span>
                            <p>{item.meaning}</p>
                            <span className="learning-thread-source">
                              {contexts.length} {contexts.length === 1 ? "encounter" : "encounters"} · latest sentence preserved <i aria-hidden="true">→</i>
                            </span>
                          </button>
                          <details className="learning-thread-detail">
                            <summary>Open the learning thread</summary>
                            <div className="evidence-receipt">
                              <span>Evidence receipt</span>
                              <strong>{item.displayWord} · {item.status}</strong>
                              <p>{evidenceReceipt(review)}</p>
                              {review ? <small>{review.evidence.due ? `Ready to ${reviewStepName(review.evidence.nextStep).toLocaleLowerCase()}.` : `Next check ${calmReviewDate(review.evidence.nextReviewAt)}.`}</small> : null}
                            </div>
                            <ol className="encounter-timeline">
                              {contexts.map((context, index) => (
                                <li key={context.id || `${context.sourceAnchor}-${index}`}>
                                  <span aria-hidden="true" />
                                  <div>
                                    <small>{calmLearningDate(context.createdAt)} · {contextSourceName(context, privateReadings)}{context.sourceType === "private_import" ? " · sense unverified" : ""}</small>
                                    <blockquote>“{context.sourceSentence}”</blockquote>
                                    <button type="button" onClick={() => void openSavedWord({
                                      ...item,
                                      meaning: context.meaning,
                                      sourceSentence: context.sourceSentence,
                                      sourceAnchor: context.sourceAnchor,
                                    })}>Return to this sentence →</button>
                                  </div>
                                </li>
                              ))}
                            </ol>
                            <button className="remove-thread" type="button" onClick={() => removeWord(item.word)}>Remove this word and its evidence…</button>
                          </details>
                        </article>
                      );
                    })}
                  </div>
                ) : (
                  <div className="empty-library"><span>◇</span><p>Your first saved word will remain attached to the sentence in which you met it.</p></div>
                )}
              </section>
            </div>
          ) : null}

          {panelMode === "profile" && currentUser ? (
            <div className="profile-panel">
              <div className="panel-header"><span>Learning profile</span><button type="button" onClick={() => setPanelMode("empty")} aria-label="Close profile">×</button></div>
              <div className="profile-intro"><span className="avatar large">{profile.displayName.slice(0, 1).toUpperCase()}</span><div><h2>{profile.displayName}</h2><p>{currentUser.email} · private learning profile</p></div></div>
              <label>
                <span>Display name</span>
                <input value={profile.displayName} onChange={(event) => setProfile({ ...profile, displayName: event.target.value })} />
              </label>
              <label>
                <span>Explain difficult words in</span>
                <select value={profile.preferredLanguage} onChange={(event) => setProfile({ ...profile, preferredLanguage: event.target.value as Language })}>
                  <option>English</option><option>Hindi</option><option>Punjabi</option>
                </select>
              </label>
              <label>
                <span>Default depth</span>
                <select value={profile.explorationDepth} onChange={(event) => setProfile({ ...profile, explorationDepth: event.target.value })}>
                  <option>Glance first</option><option>Open deeply</option>
                </select>
              </label>
              <label>
                <span>Learning assistance</span>
                <select value={profile.assistanceMode} onChange={(event) => setProfile({ ...profile, assistanceMode: event.target.value })}>
                  <option>Show me</option><option>Build with me</option><option>Test me</option>
                </select>
              </label>
              <div className="profile-stats"><div><strong>{savedWords.length}</strong><span>word threads</span></div><div><strong>{progress}%</strong><span>current reading</span></div><div><strong>{practiceAttempts.length}</strong><span>writing attempts</span></div></div>
              <button className="primary-action full" type="button" onClick={saveProfile}>Save profile</button>
              {currentUser.id.startsWith("google:") ? (
                <button className="sign-out-action" type="button" onClick={signOut}>Sign out</button>
              ) : (
                <p className="legacy-access-note">This temporary founder profile will be claimed only by the configured Google account during the backend migration.</p>
              )}
              <p className="privacy-note">Reading progress, saved words, private passages, and attempts are isolated to this Google account. Writing and pasted text are stored only after an explicit save, and private passages can be hard-deleted with their derived private context.</p>
            </div>
          ) : null}
        </aside>
      </div>

      {experienceMode === "read" && showReturn && selection ? (
        <button className="return-chip" type="button" onClick={returnToSelection}>
          <span>↩</span> Return to “{selection.word}”
        </button>
      ) : null}

      {experienceMode === "read" && panelMode === "empty" && resumeCue ? (
        <div className="reading-resume-note" role="status">
          <span aria-hidden="true">●</span>
          Continue after “{resumeCue.word}”. Your place is highlighted.
        </div>
      ) : null}

      {discoveryOpen ? (
        <div className="discovery-backdrop" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setDiscoveryOpen(false);
        }}>
          <section className="discovery-dialog" role="dialog" aria-modal="true" aria-labelledby="discovery-title">
            <header className="discovery-header">
              <div>
                <span className="section-kicker">Choose next reading</span>
                <h2 id="discovery-title">What do you want language for?</h2>
                <p>Choose an intention. The taxonomy stays underneath; the reason remains visible.</p>
              </div>
              <button type="button" onClick={() => setDiscoveryOpen(false)} aria-label="Close reading chooser">×</button>
            </header>

            <div className="intent-choices" aria-label="Reading intentions">
              {READING_INTENTS.map((intent) => (
                <button
                  key={intent.id}
                  type="button"
                  className={readingIntent === intent.id ? "active" : ""}
                  onClick={() => setReadingIntent(intent.id)}
                >
                  <strong>{intent.label}</strong>
                  <span>{intent.description}</span>
                </button>
              ))}
            </div>

            <div className="reading-results" aria-live="polite">
              <span className="section-kicker">A small, reasoned choice</span>
              {readingRecommendations.length ? readingRecommendations.map(({ reading: candidate, reason }) => (
                <article key={candidate.id}>
                  <div>
                    <span>{candidate.taxonomy.form.replace("-", " ")} · {candidate.taxonomy.readingMinutes} min</span>
                    <h3>{candidate.title}</h3>
                    <p>{reason}</p>
                  </div>
                  <button type="button" onClick={() => void openReading(candidate.id)}>Read this <span aria-hidden="true">→</span></button>
                </article>
              )) : (
                <div className="discovery-empty">
                  <p>No other maintained reading fits this intention yet. Verbalore will not pad the result with a false match.</p>
                </div>
              )}
            </div>

            <details className="maintained-shelf">
              <summary>Browse the maintained shelf</summary>
              <div>
                {READINGS.map((candidate) => (
                  <button key={candidate.id} type="button" className={candidate.id === reading.id ? "current" : ""} onClick={() => void openReading(candidate.id)}>
                    <span>{candidate.id === reading.id ? "Current" : candidate.taxonomy.form.replace("-", " ")}</span>
                    <strong>{candidate.title}</strong>
                    <small>{candidate.edition} · {candidate.provenance}</small>
                  </button>
                ))}
              </div>
            </details>
          </section>
        </div>
      ) : null}

      {importOpen ? (
        <div className="import-backdrop" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget && !savingImport) setImportOpen(false);
        }}>
          <section className="import-dialog" role="dialog" aria-modal="true" aria-labelledby="import-title">
            <header>
              <div>
                <span className="section-kicker">Private pasted reading</span>
                <h2 id="import-title">Bring language that matters to you.</h2>
                <p>Paste plain text. Verbalore will preserve it for reading and exact return; it will not analyse the passage automatically.</p>
              </div>
              <button type="button" onClick={() => setImportOpen(false)} disabled={savingImport} aria-label="Close private passage form">×</button>
            </header>

            <form onSubmit={(event) => { event.preventDefault(); void savePrivateReading(); }}>
              <label className="import-field">
                <span>Title</span>
                <input value={importTitle} onChange={(event) => setImportTitle(event.target.value)} maxLength={120} placeholder="A title only you need to recognise" />
              </label>
              <label className="import-field passage-field">
                <span>Passage</span>
                <textarea value={importSource} onChange={(event) => setImportSource(event.target.value)} maxLength={40000} rows={13} placeholder="Paste the passage here…" />
                <small>{importSource.trim().length.toLocaleString("en")} / 40,000 characters · minimum 80</small>
              </label>

              <div className="import-privacy-boundary">
                <strong>What saving does and does not do</strong>
                <p>The text is stored in your private Verbalore profile. It is not published or sent automatically to an AI model, dictionary, translation service, or pronunciation provider.</p>
                <p>Paste only material you have the right to use. You can later delete the source and its derived private context together.</p>
              </div>

              <label className="import-consent">
                <input type="checkbox" checked={importAccepted} onChange={(event) => setImportAccepted(event.target.checked)} />
                <span>I understand this private-storage boundary and choose to save this passage.</span>
              </label>

              {importError ? <p className="import-error" role="alert">{importError}</p> : null}
              <div className="import-actions">
                <button className="primary-action" type="submit" disabled={savingImport || !importAccepted || importSource.trim().length < 80}>
                  {savingImport ? "Saving privately…" : "Save and begin reading"}
                </button>
                <button className="secondary-action" type="button" onClick={() => setImportOpen(false)} disabled={savingImport}>Keep it off Verbalore</button>
              </div>
            </form>
          </section>
        </div>
      ) : null}

      {signInOpen ? (
        <div className="auth-backdrop" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget && !signingIn) setSignInOpen(false);
        }}>
          <section className="auth-dialog" role="dialog" aria-modal="true" aria-labelledby="auth-title">
            <button className="auth-close" type="button" onClick={() => setSignInOpen(false)} disabled={signingIn} aria-label="Close sign in">×</button>
            <VerbaloreMark className="auth-mark" />
            <span className="section-kicker">Your place, kept privately</span>
            <h2 id="auth-title">Read freely.<br />Sign in to keep what you learn.</h2>
            <p>The story and every word universe remain open. Google sign-in is used only to keep your progress, vocabulary, preferences, and practice across visits.</p>
            {googleClientId ? (
              <GoogleSignIn clientId={googleClientId} onCredential={handleGoogleCredential} />
            ) : authReady ? (
              <p className="auth-configuration">Google sign-in is being connected. Reading remains available in the meantime.</p>
            ) : (
              <p className="auth-configuration">Preparing secure sign-in…</p>
            )}
            {signingIn ? <p className="auth-status" role="status">Opening your private learning profile…</p> : null}
            {authError ? <p className="auth-error" role="alert">{authError}</p> : null}
            <p className="auth-privacy">Verbalore receives your basic Google identity for account access. It does not receive your password, Drive files, contacts, or email contents.</p>
          </section>
        </div>
      ) : null}

      {toast ? <div className="toast" role="status">{toast}</div> : null}
    </main>
  );
}
